(() => {
  // Prevent browser scroll restoration from interfering with dynamic updates
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
      return;
    }

    callback();
  }

  function moneyFormatter(locale, currency) {
    const normalizedCurrency = String(currency || '')
      .trim()
      .toUpperCase()
      .replace(/[^A-Z]/g, '');
    const safeCurrency = /^[A-Z]{3}$/.test(normalizedCurrency) ? normalizedCurrency : 'GBP';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: safeCurrency,
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      });
    } catch (error) {
      const symbols = {
        GBP: '£',
        USD: '$',
        EUR: '€',
      };
      const symbol = symbols[safeCurrency] || `${safeCurrency} `;

      return {
        format(value) {
          const amount = Number(value || 0);
          return `${symbol}${amount.toFixed(2)}`;
        },
      };
    }
  }

  function formatMoney(formatter, cents) {
    const value = Number(cents || 0) / 100;
    return formatter.format(value);
  }

  function formatSignedMoney(formatter, cents) {
    const amount = Number(cents || 0);
    const absolute = formatMoney(formatter, Math.abs(amount));

    if (amount > 0) {
      return `+${absolute}`;
    }

    if (amount < 0) {
      return `-${absolute}`;
    }

    return formatMoney(formatter, 0);
  }

  function initGallery(section) {
    const mainImage = section.querySelector('[data-gallery-main-image]');
    const thumbs = Array.from(section.querySelectorAll('[data-gallery-thumb]'));
    const modalThumbs = Array.from(section.querySelectorAll('[data-modal-thumb]'));
    const zoomBtn = section.querySelector('[data-gallery-zoom]');
    const modal = section.querySelector('[data-gallery-modal]');
    const thumbsViewport = section.querySelector('[data-thumbs-viewport]');
    const thumbsTrack = section.querySelector('[data-thumbs-track]');
    const prevBtn = section.querySelector('[data-thumbs-prev]');
    const nextBtn = section.querySelector('[data-thumbs-next]');
    const mainPrevBtn = section.querySelector('[data-gallery-main-prev]');
    const mainNextBtn = section.querySelector('[data-gallery-main-next]');

    if (!mainImage || thumbs.length === 0) {
      return {
        setMediaId() {},
        setVisibleMediaIds() {}, // eslint-disable-line no-unused-vars
      };
    }

    let currentIndex = 0;
    let isOpen = false;
    let thumbsScrollPosition = 0;

    function getVisibleThumbs() {
      return thumbs.filter((thumb) => !thumb.hidden);
    }

    function getVisibleImageData() {
      return getVisibleThumbs().map((thumb, index) => ({
        src: thumb.dataset.zoomSrc || thumb.dataset.imageSrc,
        alt: thumb.dataset.imageAlt || '',
        mediaId: String(thumb.dataset.mediaId || ''),
        index,
      }));
    }

    function updateMainNav() {
      const visibleThumbs = getVisibleThumbs();
      const disabled = visibleThumbs.length <= 1;

      if (mainPrevBtn) {
        mainPrevBtn.disabled = disabled;
      }

      if (mainNextBtn) {
        mainNextBtn.disabled = disabled;
      }
    }

    // Thumbnail carousel navigation
    function updateThumbsNav() {
      if (!thumbsViewport || !prevBtn || !nextBtn) return;

      const visibleThumbs = getVisibleThumbs();
      if (visibleThumbs.length <= 1) {
        prevBtn.disabled = true;
        nextBtn.disabled = true;
        return;
      }

      const viewportWidth = thumbsViewport.offsetWidth;
      const trackWidth = thumbsTrack.scrollWidth;
      const maxScroll = trackWidth - viewportWidth;

      prevBtn.disabled = thumbsScrollPosition <= 0;
      nextBtn.disabled = thumbsScrollPosition >= maxScroll - 5;
    }

    // Get current gap from CSS
    function getThumbGap() {
      if (!thumbsTrack) return 10;
      const style = window.getComputedStyle(thumbsTrack);
      return parseFloat(style.gap) || 10;
    }

    function scrollThumbs(direction) {
      if (!thumbsViewport || !thumbsTrack) return;

      const visibleThumbs = getVisibleThumbs();
      if (visibleThumbs.length === 0) return;

      const gap = getThumbGap();
      const thumbWidth = visibleThumbs[0].offsetWidth + gap;
      const viewportWidth = thumbsViewport.offsetWidth;
      const trackWidth = thumbsTrack.scrollWidth;
      const maxScroll = trackWidth - viewportWidth;

      const scrollAmount = thumbWidth * 3; // Scroll 3 thumbnails at a time

      if (direction === 'next') {
        thumbsScrollPosition = Math.min(thumbsScrollPosition + scrollAmount, maxScroll);
      } else {
        thumbsScrollPosition = Math.max(thumbsScrollPosition - scrollAmount, 0);
      }

      thumbsTrack.style.transform = `translateX(-${thumbsScrollPosition}px)`;
      updateThumbsNav();
    }

    function scrollToActiveThumb(index) {
      if (!thumbsViewport || !thumbsTrack) return;

      const visibleThumbs = getVisibleThumbs();
      const thumb = visibleThumbs[index];
      if (!thumb) return;

      const gap = getThumbGap();
      const thumbWidth = thumb.offsetWidth + gap;
      const thumbLeft = index * thumbWidth;
      const viewportWidth = thumbsViewport.offsetWidth;
      const trackWidth = thumbsTrack.scrollWidth;
      const maxScroll = trackWidth - viewportWidth;

      // Center the active thumbnail
      const targetScroll = Math.max(0, Math.min(thumbLeft - (viewportWidth / 2) + (thumbWidth / 2), maxScroll));

      thumbsScrollPosition = targetScroll;
      thumbsTrack.style.transform = `translateX(-${thumbsScrollPosition}px)`;
      updateThumbsNav();
    }

    // Touch/drag swipe support for thumbnails
    let touchStartX = 0;
    let touchEndX = 0;
    let isDragging = false;

    if (thumbsViewport) {
      thumbsViewport.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
        isDragging = true;
      }, { passive: true });

      thumbsViewport.addEventListener('touchend', (e) => {
        if (!isDragging) return;
        touchEndX = e.changedTouches[0].screenX;
        handleSwipe();
        isDragging = false;
      }, { passive: true });

      thumbsViewport.addEventListener('mousedown', (e) => {
        touchStartX = e.screenX;
        isDragging = true;
        thumbsViewport.style.cursor = 'grabbing';
      });

      thumbsViewport.addEventListener('mouseup', (e) => {
        if (!isDragging) return;
        touchEndX = e.screenX;
        handleSwipe();
        isDragging = false;
        thumbsViewport.style.cursor = 'grab';
      });

      thumbsViewport.addEventListener('mouseleave', () => {
        if (isDragging) {
          isDragging = false;
          thumbsViewport.style.cursor = 'grab';
        }
      });

      thumbsViewport.style.cursor = 'grab';
    }

    function handleSwipe() {
      const swipeThreshold = 50;
      const diff = touchStartX - touchEndX;

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          scrollThumbs('next');
        } else {
          scrollThumbs('prev');
        }
      }
    }

    // Navigation button handlers
    if (prevBtn) {
      prevBtn.addEventListener('click', () => scrollThumbs('prev'));
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => scrollThumbs('next'));
    }

    function navigateMain(direction) {
      const visibleThumbs = getVisibleThumbs();
      if (visibleThumbs.length === 0) {
        return;
      }

      const nextIndex = (currentIndex + direction + visibleThumbs.length) % visibleThumbs.length;
      const targetThumb = visibleThumbs[nextIndex];
      if (targetThumb) {
        activateThumb(targetThumb);
      }
    }

    if (mainPrevBtn) {
      mainPrevBtn.addEventListener('click', () => navigateMain(-1));
    }
    if (mainNextBtn) {
      mainNextBtn.addEventListener('click', () => navigateMain(1));
    }

    function activateThumb(targetThumb, skipModalUpdate = false) {
      const visibleThumbs = getVisibleThumbs();
      const targetIndex = visibleThumbs.indexOf(targetThumb);
      if (targetIndex !== -1) {
        currentIndex = targetIndex;
        scrollToActiveThumb(targetIndex);
      }

      thumbs.forEach((thumb) => {
        const isActive = thumb === targetThumb;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-selected', String(isActive));
      });

      const imageSrc = targetThumb.dataset.imageSrc;
      const imageSrcset = targetThumb.dataset.imageSrcset;
      const imageAlt = targetThumb.dataset.imageAlt || '';

      if (imageSrc) {
        mainImage.classList.add('is-loading');
        mainImage.src = imageSrc;
        if (imageSrcset) {
          mainImage.srcset = imageSrcset;
        }
        mainImage.alt = imageAlt;

        const clearLoadingState = () => {
          mainImage.classList.remove('is-loading');
          mainImage.removeEventListener('load', clearLoadingState);
          mainImage.removeEventListener('error', clearLoadingState);
        };

        mainImage.addEventListener('load', clearLoadingState);
        mainImage.addEventListener('error', clearLoadingState);
      }

      // Update modal if open
      if (!skipModalUpdate && isOpen) {
        updateModalImage(currentIndex);
      }

      updateMainNav();
    }

    function updateModalImage(index) {
      if (!modal) return;

      const modalImage = modal.querySelector('[data-modal-image]');
      const currentSpan = modal.querySelector('[data-modal-current]');
      const totalSpan = modal.querySelector('[data-modal-total]');
      const imageData = getVisibleImageData();

      if (!modalImage || !imageData[index]) return;

      const data = imageData[index];

      // Add loading state
      modalImage.style.opacity = '0.5';

      // Preload image
      const preloadImg = new Image();
      preloadImg.onload = () => {
        modalImage.src = data.src;
        modalImage.alt = data.alt;
        modalImage.style.opacity = '1';
      };
      preloadImg.onerror = () => {
        modalImage.src = data.src;
        modalImage.style.opacity = '1';
      };
      preloadImg.src = data.src;

      // Update counter
      if (currentSpan) {
        currentSpan.textContent = index + 1;
      }

      if (totalSpan) {
        totalSpan.textContent = imageData.length;
      }

      // Update thumbnail active state
      modalThumbs.forEach((thumb) => {
        const thumbMediaId = String(thumb.dataset.mediaId || '');
        const isActive = thumbMediaId === data.mediaId;
        thumb.classList.toggle('is-active', isActive);

        // Scroll modal thumbs to show active
        if (isActive) {
          thumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      });
    }

    function openModal() {
      if (!modal) return;

      isOpen = true;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Update modal to show current image
      updateModalImage(currentIndex);

      // Focus trap
      const closeBtn = modal.querySelector('[data-modal-close]');
      if (closeBtn) {
        closeBtn.focus();
      }
    }

    function closeModal() {
      if (!modal) return;

      isOpen = false;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      // Return focus to zoom button
      if (zoomBtn) {
        zoomBtn.focus();
      }
    }

    function navigateModal(direction) {
      const imageData = getVisibleImageData();
      if (imageData.length === 0) {
        return;
      }

      const newIndex = (currentIndex + direction + imageData.length) % imageData.length;
      currentIndex = newIndex;
      updateModalImage(newIndex);

      // Also update main gallery thumb
      const targetThumb = getVisibleThumbs()[newIndex];
      if (targetThumb) {
        activateThumb(targetThumb, true);
      }
    }

    // Thumbnail click handlers
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        activateThumb(thumb);
      });
    });

    // Zoom button click
    if (zoomBtn) {
      zoomBtn.addEventListener('click', openModal);
    }

    // Click on main image to open zoom
    if (mainImage) {
      mainImage.style.cursor = 'zoom-in';
      mainImage.addEventListener('click', openModal);

      let mainTouchStartX = 0;

      mainImage.addEventListener('touchstart', (event) => {
        mainTouchStartX = event.changedTouches[0].screenX;
      }, { passive: true });

      mainImage.addEventListener('touchend', (event) => {
        const mainTouchEndX = event.changedTouches[0].screenX;
        const diff = mainTouchStartX - mainTouchEndX;

        if (Math.abs(diff) > 50) {
          navigateMain(diff > 0 ? 1 : -1);
        }
      }, { passive: true });
    }

    // Modal event handlers
    if (modal) {
      // Close buttons
      modal.querySelectorAll('[data-modal-close]').forEach((btn) => {
        btn.addEventListener('click', closeModal);
      });

      // Navigation buttons
      const modalPrevBtn = modal.querySelector('[data-modal-prev]');
      const modalNextBtn = modal.querySelector('[data-modal-next]');

      if (modalPrevBtn) {
        modalPrevBtn.addEventListener('click', () => navigateModal(-1));
      }
      if (modalNextBtn) {
        modalNextBtn.addEventListener('click', () => navigateModal(1));
      }

      // Modal thumbnails
      modal.querySelectorAll('[data-modal-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const visibleThumbs = getVisibleThumbs();
          const index = visibleThumbs.findIndex(
            (galleryThumb) => String(galleryThumb.dataset.mediaId || '') === String(thumb.dataset.mediaId || '')
          );
          if (index === -1) {
            return;
          }

          currentIndex = index;
          updateModalImage(index);

          // Also update main gallery
          const targetThumb = visibleThumbs[index];
          if (targetThumb) {
            activateThumb(targetThumb, true);
          }
        });
      });

      // Keyboard navigation
      modal.addEventListener('keydown', (event) => {
        if (!isOpen) return;

        switch (event.key) {
          case 'Escape':
            closeModal();
            break;
          case 'ArrowLeft':
            navigateModal(-1);
            break;
          case 'ArrowRight':
            navigateModal(1);
            break;
          case 'Tab':
            // Focus trap: keep Tab cycling within modal
            const focusables = modal.querySelectorAll(
              'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );
            if (focusables.length === 0) return;

            const firstFocusable = focusables[0];
            const lastFocusable = focusables[focusables.length - 1];

            if (event.shiftKey && document.activeElement === firstFocusable) {
              event.preventDefault();
              lastFocusable.focus();
            } else if (!event.shiftKey && document.activeElement === lastFocusable) {
              event.preventDefault();
              firstFocusable.focus();
            }
            break;
        }
      });

      // Touch swipe on modal image
      let modalTouchStartX = 0;

      modal.addEventListener('touchstart', (e) => {
        modalTouchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      modal.addEventListener('touchend', (e) => {
        const modalTouchEndX = e.changedTouches[0].screenX;
        const diff = modalTouchStartX - modalTouchEndX;

        if (Math.abs(diff) > 50) {
          if (diff > 0) {
            navigateModal(1);
          } else {
            navigateModal(-1);
          }
        }
      }, { passive: true });
    }

    // Keyboard navigation for main gallery
    const thumbsContainer = section.querySelector('.gallery-thumbs');
    if (thumbsContainer) {
      thumbsContainer.addEventListener('keydown', (event) => {
        const visibleThumbs = getVisibleThumbs();
        if (visibleThumbs.length === 0) {
          return;
        }

        if (event.key === 'ArrowLeft') {
          const prevIndex = (currentIndex - 1 + visibleThumbs.length) % visibleThumbs.length;
          activateThumb(visibleThumbs[prevIndex]);
          visibleThumbs[prevIndex].focus();
        } else if (event.key === 'ArrowRight') {
          const nextIndex = (currentIndex + 1) % visibleThumbs.length;
          activateThumb(visibleThumbs[nextIndex]);
          visibleThumbs[nextIndex].focus();
        }
      });
    }

    // Initialize navigation state
    updateThumbsNav();
    updateMainNav();

    // Handle resize with debouncing to prevent scroll issues on mobile
    let galleryResizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(galleryResizeTimeout);
      galleryResizeTimeout = setTimeout(() => {
        updateThumbsNav();
        updateMainNav();
        scrollToActiveThumb(currentIndex);
      }, 150);
    });

    // ── Colour-based gallery filtering ─────────────────────────────────
    // Show only the images whose alt tag matches the selected colour. Runs on
    // init (which also covers full-section re-renders for >250-variant
    // products) and whenever a colour swatch is clicked, so the gallery stays
    // in sync with the selected colour even when the client-side variant
    // lookup is skipped and updateVariantUi never fires.
    function normalizeForCompare(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    function altMatchesColorValue(alt, colorNorm) {
      if (!colorNorm) return false;
      // An image alt may be a single tag or a comma/pipe/slash list of tags.
      const tokens = String(alt || '').toLowerCase().split(/[,|/;]/);
      for (const token of tokens) {
        const tokenNorm = normalizeForCompare(token);
        if (!tokenNorm) continue;
        if (tokenNorm === colorNorm || tokenNorm.startsWith(colorNorm + '-')) {
          return true;
        }
      }
      return false;
    }

    function getSelectedColorValue() {
      // Prefer the colour label the theme keeps in sync, then fall back to the
      // selected swatch button's value.
      const currentNodes = section.querySelectorAll('[data-product-color-current]');
      for (const node of currentNodes) {
        const value = (node.textContent || '').trim();
        if (value) return value;
      }
      const scope = section.querySelector('.color-opts-grid.is-active')
        || section.querySelector('.color-opts-grid')
        || section;
      const selectedBtn = scope.querySelector('.color-opt.is-selected, .color-opt[aria-pressed="true"]')
        || section.querySelector('.color-opt.is-selected, .color-opt[aria-pressed="true"]');
      return selectedBtn ? (selectedBtn.dataset.optionValue || '').trim() : '';
    }

    let colorFilterTimer = null;
    function applyColorGalleryFilter() {
      const colorValue = getSelectedColorValue();
      const colorNorm = normalizeForCompare(colorValue);
      const hasColor = colorNorm !== '';

      let matchCount = 0;
      let firstVisibleThumb = null;

      thumbs.forEach((thumb) => {
        if (thumb.hasAttribute('data-static-thumb')) return;
        let isVisible = true;
        if (hasColor) {
          const alt = thumb.dataset.imageAlt
            || (thumb.querySelector('img') ? thumb.querySelector('img').alt : '')
            || '';
          isVisible = altMatchesColorValue(alt, colorNorm);
        }
        thumb.hidden = !isVisible;
        if (isVisible) {
          matchCount += 1;
          if (!firstVisibleThumb) {
            firstVisibleThumb = thumb;
          }
        }
      });

      modalThumbs.forEach((thumb) => {
        let isVisible = true;
        if (hasColor) {
          const alt = thumb.dataset.alt
            || (thumb.querySelector('img') ? thumb.querySelector('img').alt : '')
            || '';
          isVisible = altMatchesColorValue(alt, colorNorm);
        }
        thumb.hidden = !isVisible;
      });

      // Safety: if nothing matches the selected colour (e.g. images have no
      // colour-specific alt), keep the gallery populated rather than empty.
      if (hasColor && matchCount === 0) {
        thumbs.forEach((thumb) => {
          if (!thumb.hasAttribute('data-static-thumb')) {
            thumb.hidden = false;
            if (!firstVisibleThumb) firstVisibleThumb = thumb;
          }
        });
        modalThumbs.forEach((thumb) => { thumb.hidden = false; });
      }

      thumbsScrollPosition = 0;
      if (thumbsTrack) {
        thumbsTrack.style.transform = 'translateX(0)';
      }

      updateThumbsNav();
      updateMainNav();

      const visibleThumbs = getVisibleThumbs();
      const activeStillVisible = visibleThumbs.some((t) => t.classList.contains('is-active'));
      if (!activeStillVisible && firstVisibleThumb) {
        activateThumb(firstVisibleThumb, false);
      } else if (visibleThumbs.length > 0) {
        scrollToActiveThumb(currentIndex);
      }
    }

    function scheduleColorFilter() {
      clearTimeout(colorFilterTimer);
      colorFilterTimer = setTimeout(applyColorGalleryFilter, 0);
    }

    // Initial filter (and after every section re-render via initProductSection).
    scheduleColorFilter();

    // Re-filter the instant a colour swatch is clicked.
    section.querySelectorAll('.color-opt').forEach((button) => {
      button.addEventListener('click', scheduleColorFilter);
    });

    // Keep in sync with theme-driven variant changes.
    section.addEventListener('variant:change', scheduleColorFilter);

    return {
      setMediaId(mediaId) {
        if (!mediaId) {
          return;
        }

        const match = thumbs.find((thumb) => thumb.dataset.mediaId === String(mediaId));

        if (match) {
          activateThumb(match);
        }
      },
      setVisibleMediaIds(mediaIds, activeMediaId, colorValue = '') {
        const normalizedIds = Array.isArray(mediaIds)
          ? mediaIds.map((id) => String(id)).filter(Boolean)
          : [];
        const allowedMediaIds = new Set(normalizedIds);
        const hasGroupFilter = allowedMediaIds.size > 0;

        function altMatchesColor(alt) {
          if (!colorValue || !alt) return false;
          const normalize = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
          const altNorm = normalize(alt);
          const colorNorm = normalize(colorValue);
          if (!altNorm || !colorNorm) return false;
          if (altNorm === colorNorm) return true;
          if (altNorm.startsWith(colorNorm + '-')) return true;
          return false;
        }

        thumbs.forEach((thumb) => {
          if (thumb.hasAttribute('data-static-thumb')) return;
          let isVisible;
          if (hasGroupFilter) {
            isVisible = allowedMediaIds.has(String(thumb.dataset.mediaId || ''));
          } else if (colorValue) {
            const alt = thumb.dataset.imageAlt || thumb.querySelector('img')?.alt || '';
            isVisible = altMatchesColor(alt);
          } else {
            isVisible = true;
          }
          thumb.hidden = !isVisible;
        });

        modalThumbs.forEach((thumb) => {
          let isVisible;
          if (hasGroupFilter) {
            isVisible = allowedMediaIds.has(String(thumb.dataset.mediaId || ''));
          } else if (colorValue) {
            const alt = thumb.dataset.alt || thumb.querySelector('img')?.alt || '';
            isVisible = altMatchesColor(alt);
          } else {
            isVisible = true;
          }
          thumb.hidden = !isVisible;
        });

        thumbsScrollPosition = 0;
        if (thumbsTrack) {
          thumbsTrack.style.transform = 'translateX(0)';
        }

        const visibleThumbs = getVisibleThumbs();
        const nonStaticVisible = visibleThumbs.filter((t) => !t.hasAttribute('data-static-thumb'));
        const preferredThumb = activeMediaId ? visibleThumbs.find(
          (thumb) => String(thumb.dataset.mediaId || '') === String(activeMediaId)
        ) : null;
        const nextThumb = preferredThumb || nonStaticVisible[0] || thumbs[0];

        updateThumbsNav();

        if (nextThumb) {
          activateThumb(nextThumb, false);
        }
      },
      openZoom() {
        openModal();
      },
    };
  }

  function initVariantTabs(section) {
    const tabs = Array.from(section.querySelectorAll('[data-variant-tab]'));
    const panels = Array.from(section.querySelectorAll('.variant-panel'));
    const tabList = section.querySelector('.variant-filter-bar');

    if (tabs.length === 0 || panels.length === 0) {
      return;
    }

    function updateVisibleTabCount() {
      if (!tabList) {
        return;
      }

      const visibleTabs = tabs.filter((tab) => window.getComputedStyle(tab).display !== 'none');
      // > 3 tabs: show 3 full + partial 4th (3.3)
      // 3 tabs: full width each (3)
      // 2 tabs: split evenly (2)
      // 1 tab: full width (1)
      const visibleCount = visibleTabs.length > 3 ? 3.3 : Math.max(visibleTabs.length, 1);
      tabList.style.setProperty('--visible-tab-count', String(visibleCount));
    }

    function getInitialTab(preferMobileSizeTab = false) {
      const visibleTabs = tabs.filter((tab) => window.getComputedStyle(tab).display !== 'none');
      const activeTab =
        visibleTabs.find((tab) => tab.classList.contains('is-active')) ||
        visibleTabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ||
        visibleTabs[0] ||
        tabs[0];

      if (!preferMobileSizeTab || !window.matchMedia('(max-width: 749px)').matches) {
        return activeTab;
      }

      const mobileSizeTab = visibleTabs.find((tab) => {
        const label = (tab.querySelector('.variant-tab-label')?.textContent || '').trim().toLowerCase();
        return label.includes('size');
      });

      return mobileSizeTab || activeTab;
    }

    function openTab(tab, moveFocus = false, scrollTabIntoView = false) {
      const targetId = tab.dataset.tabTarget;

      tabs.forEach((item) => {
        const isActive = item === tab;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-selected', String(isActive));
        item.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      panels.forEach((panel) => {
        const isActive = panel.id === targetId;
        panel.classList.toggle('is-active', isActive);
        if (isActive) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });

      if (moveFocus) {
        tab.focus({ preventScroll: true });
      }

      if (scrollTabIntoView && window.matchMedia('(max-width: 749px)').matches) {
        tab.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        openTab(tab, false, true);
      });

      // Keyboard navigation
      tab.addEventListener('keydown', (event) => {
        let nextIndex = index;

        if (event.key === 'ArrowRight') {
          nextIndex = (index + 1) % tabs.length;
        } else if (event.key === 'ArrowLeft') {
          nextIndex = (index - 1 + tabs.length) % tabs.length;
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = tabs.length - 1;
        } else if (event.key === 'Enter' || event.key === ' ') {
          openTab(tab, false);
          return;
        } else {
          return;
        }

        event.preventDefault();
        openTab(tabs[nextIndex], true, true);
      });
    });

    let wasMobileViewport = window.matchMedia('(max-width: 749px)').matches;

    function syncTabsForViewport(isInitialSync = false) {
      const isMobileViewport = window.matchMedia('(max-width: 749px)').matches;
      updateVisibleTabCount();

      if (isInitialSync && isMobileViewport) {
        const sizeTab = getInitialTab(true);
        if (sizeTab && !sizeTab.classList.contains('is-active')) {
          openTab(sizeTab, false);
        }
        wasMobileViewport = isMobileViewport;
        return;
      }

      if (wasMobileViewport === isMobileViewport) {
        return;
      }

      const currentTab =
        tabs.find((tab) => tab.classList.contains('is-active')) ||
        tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ||
        getInitialTab(isMobileViewport);

      if (currentTab) {
        openTab(currentTab, false);
      }

      wasMobileViewport = isMobileViewport;
    }

    syncTabsForViewport(true);

    // Debounce resize handler to prevent scroll issues on mobile
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(syncTabsForViewport, 150);
    });

    // --- Drag-to-scroll + scroll cue ---
    if (tabList) {
      const wrap = tabList.closest('[data-variant-tabs]');
      const cue = wrap ? wrap.querySelector('[data-variant-scroll-cue]') : null;

      function updateCueVisibility() {
        if (!cue) return;
        const overflowing = tabList.scrollWidth - tabList.clientWidth > 4;
        if (!overflowing) {
          cue.hidden = true;
          return;
        }
        cue.hidden = false;
        const nearEnd = tabList.scrollLeft + tabList.clientWidth >= tabList.scrollWidth - 12;
        cue.classList.toggle('is-faded', nearEnd);
      }

      tabList.addEventListener('scroll', updateCueVisibility, { passive: true });
      window.addEventListener('resize', updateCueVisibility);
      requestAnimationFrame(updateCueVisibility);

      if (cue) {
        cue.addEventListener('click', () => {
          tabList.scrollBy({ left: Math.max(120, tabList.clientWidth * 0.6), behavior: 'smooth' });
        });
      }

      // Mouse drag-to-scroll (touch already works natively).
      // We only flag .is-dragging AFTER movement crosses the threshold, so a
      // plain click on a tab still works normally.
      const DRAG_THRESHOLD = 6;
      let dragState = null;

      function onDragMove(e) {
        if (!dragState) return;
        const dx = e.clientX - dragState.startX;
        if (!dragState.dragging) {
          if (Math.abs(dx) < DRAG_THRESHOLD) return;
          dragState.dragging = true;
          tabList.classList.add('is-dragging');
        }
        tabList.scrollLeft = dragState.startScroll - dx;
        e.preventDefault();
      }

      function onDragEnd() {
        if (!dragState) return;
        const wasDragging = dragState.dragging;
        if (wasDragging) {
          tabList.classList.remove('is-dragging');
          // Suppress the click that follows a real drag so the released-on tab doesn't activate.
          const suppress = (evt) => {
            evt.stopPropagation();
            evt.preventDefault();
            tabList.removeEventListener('click', suppress, true);
          };
          tabList.addEventListener('click', suppress, true);
          // Safety: if no click follows shortly, remove the listener.
          setTimeout(() => tabList.removeEventListener('click', suppress, true), 300);
        }
        document.removeEventListener('pointermove', onDragMove);
        document.removeEventListener('pointerup', onDragEnd);
        document.removeEventListener('pointercancel', onDragEnd);
        dragState = null;
      }

      tabList.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse') return; // touch/pen use native scroll
        if (e.button !== 0) return;
        dragState = {
          startX: e.clientX,
          startScroll: tabList.scrollLeft,
          dragging: false,
        };
        document.addEventListener('pointermove', onDragMove);
        document.addEventListener('pointerup', onDragEnd);
        document.addEventListener('pointercancel', onDragEnd);
      });
    }
  }

  function initInfoTabs(root = document) {
    const groups = Array.from(root.querySelectorAll('[data-content-tabs]'));

    groups.forEach((group) => {
      const tabs = Array.from(group.querySelectorAll('[data-tab-trigger]'));
      const panels = Array.from(group.querySelectorAll('[data-tab-panel]'));

      if (tabs.length === 0 || panels.length === 0) {
        return;
      }

      function setActiveTab(nextTab, moveFocus) {
        const targetPanelId = nextTab.getAttribute('aria-controls');

        tabs.forEach((tab) => {
          const isActive = tab === nextTab;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', String(isActive));
          tab.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        panels.forEach((panel) => {
          const isActive = panel.id === targetPanelId;
          panel.classList.toggle('is-active', isActive);
          if (isActive) {
            panel.removeAttribute('hidden');
          } else {
            panel.setAttribute('hidden', '');
          }
        });

        if (moveFocus) {
          nextTab.focus();
        }
      }

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => {
          setActiveTab(tab, false);
        });

        tab.addEventListener('keydown', (event) => {
          let nextIndex = index;

          if (event.key === 'ArrowRight') {
            nextIndex = (index + 1) % tabs.length;
          } else if (event.key === 'ArrowLeft') {
            nextIndex = (index - 1 + tabs.length) % tabs.length;
          } else if (event.key === 'Home') {
            nextIndex = 0;
          } else if (event.key === 'End') {
            nextIndex = tabs.length - 1;
          } else if (event.key === 'Enter' || event.key === ' ') {
            setActiveTab(tab, false);
            return;
          } else {
            return;
          }

          event.preventDefault();
          setActiveTab(tabs[nextIndex], true);
        });
      });

      const activeTab =
        tabs.find((tab) => tab.classList.contains('is-active')) ||
        tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ||
        tabs[0];

      setActiveTab(activeTab, false);
    });
  }

  function initFaq(root = document) {
    const items = Array.from(root.querySelectorAll('[data-faq-item]'));

    if (items.length === 0) {
      return;
    }

    function closeItem(item) {
      const trigger = item.querySelector('[data-faq-trigger]');
      const panelId = trigger ? trigger.getAttribute('aria-controls') : '';
      const panel = panelId ? document.getElementById(panelId) : null;

      item.classList.remove('is-open');
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      }
      if (panel) {
        panel.setAttribute('hidden', '');
      }
    }

    function openItem(item) {
      const trigger = item.querySelector('[data-faq-trigger]');
      const panelId = trigger ? trigger.getAttribute('aria-controls') : '';
      const panel = panelId ? document.getElementById(panelId) : null;

      item.classList.add('is-open');
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
      }
      if (panel) {
        panel.removeAttribute('hidden');
      }
    }

    items.forEach((item) => {
      const trigger = item.querySelector('[data-faq-trigger]');

      if (!trigger) {
        return;
      }

      trigger.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-open');

        items.forEach((faqItem) => {
          closeItem(faqItem);
        });

        if (!isOpen) {
          openItem(item);
        }
      });
    });
  }

  function initDimensionsTabs(root = document) {
    // UK STANDARD BED SIZES
    const DIMS_UK = [
      { id:'small-single', name:'Small Single', imp:'2\'6" × 6\'3"', w:75,  l:190 },
      { id:'single',       name:'Single',       imp:'3\'0" × 6\'3"', w:90,  l:190 },
      { id:'small-double', name:'Small Double', imp:'4\'0" × 6\'3"', w:120, l:190 },
      { id:'double',       name:'Double',       imp:'4\'6" × 6\'3"', w:135, l:190, popular:true },
      { id:'king',         name:'King',         imp:'5\'0" × 6\'6"', w:150, l:200 },
      { id:'super-king',   name:'Super King',   imp:'6\'0" × 6\'6"', w:180, l:200 },
    ];

    // PRODUCT TYPE CONFIGS
    const DIMS_TYPES = {
      'divan-drawer': {
        label:'Divan with Drawers',
        fpAdd:{ w:6, l:3 },
        baseH:39,
        getDrawers: (w) => w <= 90 ? '1 or 2' : '2 or 4'
      },
      'ottoman': {
        label:'Ottoman',
        fpAdd:{ w:6, l:8 },
        baseH:40,
        getLift: () => 'End-opening gas lift'
      },
      'bed-frame': {
        label:'Bed Frame',
        fpAdd:{ w:8, l:15 },
        baseH:35,
        getClearance: () => '20–28'
      },
    };

    // HEADBOARD OPTIONS
    const DIMS_HB = [
      { id:'20', label:'20" (50 cm)', height:50 },
      { id:'26', label:'26" (66 cm)', height:66 },
      { id:'32', label:'32" (81 cm)', height:81 },
      { id:'floor', label:'Floor Standing (135 cm)', height:135 },
    ];

    // Current state
    let currentType = 'divan-drawer';
    let currentSize = 'double';
    let currentHB = '20';
    let useImperial = false; // false = cm, true = ft/inches

    // DOM elements
    const typeTabs = Array.from(root.querySelectorAll('.dims-type-tab'));
    const sizePills = Array.from(root.querySelectorAll('.dims-size-pill'));
    const hbPills = Array.from(root.querySelectorAll('.dims-hb-pill'));
    const sizeSubtitle = root.querySelector('.dims-size-subtitle');
    const unitsToggle = root.querySelector('[data-units-toggle]');

    // Convert cm to feet and inches
    function cmToImperial(cm) {
      const totalInches = cm / 2.54;
      const feet = Math.floor(totalInches / 12);
      const inches = Math.round(totalInches % 12);
      return { feet, inches, display: `${feet}'${inches}"` };
    }

    // Format dimension based on current unit
    function formatDim(cm) {
      if (useImperial) {
        return cmToImperial(cm).display;
      }
      return `${cm} cm`;
    }

    // Get current size data
    function getSizeData() {
      return DIMS_UK.find(s => s.id === currentSize) || DIMS_UK[3];
    }

    // Get current type data
    function getTypeData() {
      return DIMS_TYPES[currentType] || DIMS_TYPES['divan-drawer'];
    }

    // Get current headboard data
    function getHBData() {
      return DIMS_HB.find(h => h.id === currentHB) || DIMS_HB[0];
    }

    // Update the dimensions display
    function updateDimensionsDisplay() {
      const size = getSizeData();
      const type = getTypeData();
      const hb = getHBData();

      if (!size) return;

      // Calculate footprint
      const fpW = size.w + type.fpAdd.w;
      const fpL = size.l + type.fpAdd.l;

      // Update subtitle
      if (sizeSubtitle) {
        if (useImperial) {
          sizeSubtitle.textContent = `${size.imp} - Mattress: ${cmToImperial(size.w).display} x ${cmToImperial(size.l).display}`;
        } else {
          sizeSubtitle.textContent = `${size.imp} - Mattress: ${size.w} x ${size.l} cm`;
        }
      }

      // Update SVG diagram dimensions
      const svgWidth = root.querySelector('.dims-dim-text:first-of-type');
      const svgHeight = root.querySelector('.dims-dim-text:last-of-type');

      if (svgWidth) {
        svgWidth.textContent = formatDim(fpW);
      }
      if (svgHeight) {
        svgHeight.textContent = formatDim(fpL);
      }

      // Update specs panel
      const specGroups = root.querySelectorAll('.dims-spec-group');
      if (specGroups.length >= 2) {
        // Mattress size
        const mattressSpec = specGroups[0].querySelector('.dims-spec-value');
        if (mattressSpec) {
          if (useImperial) {
            mattressSpec.innerHTML = `${cmToImperial(size.w).display} x ${cmToImperial(size.l).display} <span class="alt">(${size.imp})</span>`;
          } else {
            mattressSpec.innerHTML = `${size.w} cm x ${size.l} cm <span class="alt">(${size.imp})</span>`;
          }
        }
        // Base footprint
        const footprintSpec = specGroups[1].querySelector('.dims-spec-value');
        if (footprintSpec) {
          footprintSpec.innerHTML = useImperial
            ? `${cmToImperial(fpW).display} x ${cmToImperial(fpL).display}`
            : `${fpW} cm x ${fpL} cm`;
        }
      }

      // Update the spec dual section (base height and drawers/lift/clearance)
      const specDual = root.querySelector('.dims-spec-dual');
      if (specDual) {
        const dualSpecs = specDual.querySelectorAll('.dims-spec-group');
        if (dualSpecs.length >= 2) {
          // Base height
          const baseHeightSpec = dualSpecs[0].querySelector('.dims-spec-value');
          if (baseHeightSpec) {
            baseHeightSpec.textContent = formatDim(type.baseH);
          }
          // Type-specific value (drawers/lift/clearance)
          const typeSpec = dualSpecs[1].querySelector('.dims-spec-value');
          if (typeSpec) {
            if (type.getDrawers) {
              typeSpec.textContent = type.getDrawers(size.w);
            } else if (type.getLift) {
              typeSpec.textContent = type.getLift();
            } else if (type.getClearance) {
              typeSpec.textContent = type.getClearance();
            }
          }
        }
      }

      // Update explainer values
      const explainerItems = root.querySelectorAll('.dims-explain-item');
      if (explainerItems.length >= 4) {
        // Width
        const widthVal = explainerItems[0].querySelector('.et-val');
        if (widthVal) widthVal.textContent = formatDim(fpW);
        // Length
        const lengthVal = explainerItems[1].querySelector('.et-val');
        if (lengthVal) lengthVal.textContent = formatDim(fpL);
        // Base Height
        const baseHeightVal = explainerItems[2].querySelector('.et-val');
        if (baseHeightVal) baseHeightVal.textContent = formatDim(type.baseH);
        // Headboard Height
        const hbHeightVal = explainerItems[3].querySelector('.et-val');
        if (hbHeightVal) hbHeightVal.textContent = formatDim(hb.height);
      }

      // Update compare row active state
      root.querySelectorAll('.dims-compare-card').forEach((card, index) => {
        card.classList.toggle('active', DIMS_UK[index]?.id === currentSize);
      });
    }

    // Helper to activate a type tab
    function activateTypeTab(tab) {
      typeTabs.forEach((t) => {
        t.classList.remove('active');
        t.setAttribute('aria-selected', 'false');
        t.setAttribute('aria-pressed', 'false');
      });
      tab.classList.add('active');
      tab.setAttribute('aria-selected', 'true');
      tab.setAttribute('aria-pressed', 'true');
      const tabText = tab.textContent.trim();
      Object.entries(DIMS_TYPES).forEach(([key, value]) => {
        if (value.label === tabText) {
          currentType = key;
        }
      });
      updateDimensionsDisplay();
    }

    // Type tabs click handler
    typeTabs.forEach((tab) => {
      tab.addEventListener('click', () => activateTypeTab(tab));
    });

    // Type tabs keyboard navigation (arrow keys, home, end)
    const tablist = root.querySelector('.dims-type-tabs');
    if (tablist) {
      tablist.addEventListener('keydown', (e) => {
        const currentIndex = typeTabs.findIndex(t => t === document.activeElement);
        if (currentIndex === -1) return;

        let newIndex = currentIndex;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          newIndex = (currentIndex + 1) % typeTabs.length;
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          newIndex = (currentIndex - 1 + typeTabs.length) % typeTabs.length;
        } else if (e.key === 'Home') {
          e.preventDefault();
          newIndex = 0;
        } else if (e.key === 'End') {
          e.preventDefault();
          newIndex = typeTabs.length - 1;
        }

        if (newIndex !== currentIndex) {
          typeTabs[newIndex].focus();
          activateTypeTab(typeTabs[newIndex]);
        }
      });
    }

    // Size pills click handler
    sizePills.forEach((pill) => {
      pill.addEventListener('click', () => {
        sizePills.forEach((p) => {
          p.classList.remove('active');
          p.setAttribute('aria-checked', 'false');
        });
        pill.classList.add('active');
        pill.setAttribute('aria-checked', 'true');

        // Get size name from first text node or data attribute, excluding tag spans
        const sizeName = (pill.childNodes[0]?.textContent || pill.textContent).replace(/Popular/gi, '').trim();
        const sizeData = DIMS_UK.find(s => s.name === sizeName);
        if (sizeData) {
          currentSize = sizeData.id;
        }

        updateDimensionsDisplay();
      });
    });

    // Headboard height pills click handler
    hbPills.forEach((pill) => {
      pill.addEventListener('click', () => {
        hbPills.forEach((p) => {
          p.classList.remove('active');
          p.setAttribute('aria-checked', 'false');
        });
        pill.classList.add('active');
        pill.setAttribute('aria-checked', 'true');

        // Find matching headboard
        const hbLabel = pill.textContent.trim();
        const hbData = DIMS_HB.find(h => h.label === hbLabel);
        if (hbData) {
          currentHB = hbData.id;
        }
        updateDimensionsDisplay();
      });
    });

    // Units toggle click handler
    if (unitsToggle) {
      unitsToggle.addEventListener('click', () => {
        useImperial = !useImperial;
        unitsToggle.setAttribute('aria-checked', String(useImperial));
        unitsToggle.classList.toggle('is-imperial', useImperial);
        updateDimensionsDisplay();
      });
    }

    // Compare cards click handler - sync with size pills
    const compareCards = Array.from(root.querySelectorAll('.dims-compare-card'));
    compareCards.forEach((card) => {
      card.addEventListener('click', () => {
        const sizeId = card.dataset.compareSize;
        const sizeData = DIMS_UK.find(s => s.id === sizeId);
        if (!sizeData) return;

        currentSize = sizeId;

        // Update compare cards active state
        compareCards.forEach((c) => {
          const isActive = c === card;
          c.classList.toggle('active', isActive);
          c.setAttribute('aria-pressed', String(isActive));
        });

        // Update size pills to match
        sizePills.forEach((pill) => {
          const pillName = (pill.childNodes[0]?.textContent || pill.textContent).replace(/Popular/gi, '').trim();
          const isActive = pillName === sizeData.name;
          pill.classList.toggle('active', isActive);
          pill.setAttribute('aria-checked', String(isActive));
        });

        // Scroll size pills into view if needed
        const activePill = sizePills.find(p => p.classList.contains('active'));
        if (activePill) {
          activePill.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }

        updateDimensionsDisplay();
      });
    });

    // Initial update
    updateDimensionsDisplay();
  }

  function getDimensionsForSize(size) {
    const subtitleMap = {
      'Small Single': '2\'6" x 6\'3" - Mattress: 75 x 190 cm',
      'Single': '3\'0" x 6\'3" - Mattress: 90 x 190 cm',
      'Small Double': '4\'0" x 6\'3" - Mattress: 120 x 190 cm',
      'Double': '4\'6" x 6\'3" - Mattress: 135 x 190 cm',
      'King': '5\'0" x 6\'6" - Mattress: 150 x 200 cm',
      'Super King': '6\'0" x 6\'6" - Mattress: 180 x 200 cm',
    };
    return {
      subtitle: subtitleMap[size.replace('Popular', '').trim()] || size,
    };
  }

  function initChat(root = document) {
    const messages = root.querySelector('#chat-msgs');
    const input = root.querySelector('#chat-input');
    const sendButton = root.querySelector('#chat-send');
    const suggestButtons = Array.from(root.querySelectorAll('[data-suggest]'));
    // Read webhook URL from theme settings (data attribute) instead of hardcoding
    const section = root.querySelector('[data-product-section]');
    const WEBHOOK_URL = section ? section.dataset.chatWebhook : '';

    if (!messages) {
      return;
    }

    function pushMessage(text, type) {
      const message = document.createElement('div');
      message.className = `chat-msg chat-msg-${type}`;
      message.textContent = text;
      messages.append(message);
      messages.scrollTop = messages.scrollHeight;
    }

    function pushTyping() {
      const typing = document.createElement('div');
      typing.className = 'chat-msg chat-msg-bot chat-msg-typing';
      typing.setAttribute('aria-label', 'Sleep expert is typing');
      typing.innerHTML = '<span class="chat-dot"></span><span class="chat-dot"></span><span class="chat-dot"></span>';
      messages.append(typing);
      messages.scrollTop = messages.scrollHeight;
      return typing;
    }

    function extractChatReply(payload) {
      if (!payload) {
        return '';
      }

      if (typeof payload === 'string') {
        return payload.trim();
      }

      if (Array.isArray(payload)) {
        for (const item of payload) {
          const nested = extractChatReply(item);
          if (nested) {
            return nested;
          }
        }
        return '';
      }

      if (typeof payload === 'object') {
        const preferredKeys = ['reply', 'message', 'text', 'output', 'response', 'answer', 'content'];
        for (const key of preferredKeys) {
          if (key in payload) {
            const nested = extractChatReply(payload[key]);
            if (nested) {
              return nested;
            }
          }
        }

        for (const value of Object.values(payload)) {
          const nested = extractChatReply(value);
          if (nested) {
            return nested;
          }
        }
      }

      return '';
    }

    async function sendMessage(text) {
      const trimmed = String(text || '').trim();
      if (!trimmed) {
        return;
      }

      pushMessage(trimmed, 'user');
      if (input) {
        input.value = '';
      }

      const typing = pushTyping();

      try {
        const response = await fetch(WEBHOOK_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: trimmed,
            timestamp: new Date().toISOString(),
            sessionId: getSessionId(),
          }),
        });

        if (response.ok) {
          const responseText = await response.text();
          typing.remove();

          let reply = '';
          if (responseText) {
            try {
              const data = JSON.parse(responseText);
              reply = extractChatReply(data);
            } catch {
              reply = responseText.trim();
            }
          }

          if (!reply) {
            pushMessage('No reply came back from the assistant. Please try again.', 'bot');
            return;
          }

          pushMessage(reply, 'bot');
        } else {
          throw new Error('Network error');
        }
      } catch (error) {
        typing.remove();
        pushMessage('Sorry, there was an error connecting to our sleep experts. Please try again or call us directly.', 'bot');
        console.error('Chat webhook error:', error);
      }
    }

    // Generate a simple session ID
    function getSessionId() {
      let sessionId = sessionStorage.getItem('chat_session_id');
      if (!sessionId) {
        sessionId = 'session_' + Math.random().toString(36).substr(2, 9);
        sessionStorage.setItem('chat_session_id', sessionId);
      }
      return sessionId;
    }

    suggestButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const value = button.dataset.suggest;
        sendMessage(value);
      });
    });

    if (sendButton) {
      sendButton.addEventListener('click', () => {
        sendMessage(input ? input.value : '');
      });
    }

    if (input) {
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          sendMessage(input.value);
        }
      });
    }
  }

  function initPageAnchors(root = document) {
    root.querySelectorAll('[data-scroll-chat]').forEach((element) => {
      element.addEventListener('click', (event) => {
        event.preventDefault();
        document.getElementById('sleep-expert-chat')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    root.querySelectorAll('[data-focus-chat]').forEach((element) => {
      element.addEventListener('click', (event) => {
        event.preventDefault();
        document.getElementById('sleep-expert-chat')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const input = document.getElementById('chat-input');
        if (input) {
          window.setTimeout(() => {
            input.focus();
          }, 250);
        }
      });
    });

    root.querySelectorAll('[data-scroll-to-atc]').forEach((element) => {
      element.addEventListener('click', (event) => {
        event.preventDefault();
        root.querySelector('[data-add-to-cart]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });
  }

  function initRevealAnimation(root = document) {
    const targets = Array.from(root.querySelectorAll('.afu'));
    if (targets.length === 0) {
      return;
    }

    if (typeof window.IntersectionObserver === 'undefined') {
      targets.forEach((target) => {
        target.classList.add('is-visible');
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.12,
      }
    );

    targets.forEach((target) => {
      observer.observe(target);
    });
  }

  function initStickyAtc(section, form) {
    const stickyBar = section.querySelector('[data-sticky-atc]');
    const stickySubmit = section.querySelector('[data-sticky-atc-submit]');
    const anchor = section.querySelector('[data-atc-anchor]');

    if (stickySubmit) {
      stickySubmit.addEventListener('click', () => {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.submit();
        }
      });
    }

    if (!stickyBar || !anchor || typeof window.IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
        });
      },
      {
        threshold: 0.2,
      }
    );

    observer.observe(anchor);
  }

  // Wishlist Management with localStorage persistence
  const WISHLIST_KEY = 'shopify_wishlist';

  function getWishlist() {
    try {
      const data = localStorage.getItem(WISHLIST_KEY);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  function saveWishlist(wishlist) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(wishlist));
      // Dispatch custom event for other components
      window.dispatchEvent(
        new CustomEvent('wishlist:updated', { detail: { wishlist } })
      );
    } catch (e) {
      console.error('Failed to save wishlist:', e);
    }
  }

  function isInWishlist(productId) {
    const wishlist = getWishlist();
    return wishlist.includes(String(productId));
  }

  function addToWishlist(productId, productHandle) {
    const wishlist = getWishlist();
    if (!wishlist.includes(String(productId))) {
      wishlist.push(String(productId));
      saveWishlist(wishlist);
      showToast('Added to wishlist');
      return true;
    }
    return false;
  }

  function removeFromWishlist(productId) {
    let wishlist = getWishlist();
    wishlist = wishlist.filter((id) => id !== String(productId));
    saveWishlist(wishlist);
    showToast('Removed from wishlist');
    return wishlist;
  }

  function showToast(message) {
    // Remove existing toast
    const existingToast = document.querySelector('.wishlist-toast');
    if (existingToast) {
      existingToast.remove();
    }

    const toast = document.createElement('div');
    toast.className = 'wishlist-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = `
      <span class="mi" aria-hidden="true">favorite</span>
      <span>${message}</span>
    `;
    document.body.appendChild(toast);

    // Trigger animation
    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    // Remove after delay
    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 300);
    }, 2500);
  }

  function updateWishlistButtons(section) {
    // Initialize visual state only - click handling is done by global wishlist.js
    section.querySelectorAll('[data-wishlist-btn], [data-wishlist-btn-secondary]').forEach((button) => {
      const productId = button.dataset.productId;
      if (productId) {
        updateWishlistButtonState(button, productId);
      }
    });
  }

  function updateWishlistButtonState(button, productId) {
    // Use global Wishlist API if available, fallback to local
    const isIn = window.Wishlist ? window.Wishlist.isIn(productId) : isInWishlist(productId);
    if (isIn) {
      button.classList.add('is-active');
      button.setAttribute('aria-pressed', 'true');
    } else {
      button.classList.remove('is-active');
      button.setAttribute('aria-pressed', 'false');
    }
    updateTooltip(button, productId);
  }

  function updateTooltip(button, productId) {
    // Use global Wishlist API if available, fallback to local
    const isIn = window.Wishlist ? window.Wishlist.isIn(productId) : isInWishlist(productId);
    const tooltip = button.querySelector('.gallery-wish-tooltip');
    if (tooltip) {
      tooltip.textContent = isIn ? 'Remove from Wishlist' : 'Add to Wishlist';
    }
    // Also update secondary button label
    const label = button.querySelector('.btn-wishlist-label');
    if (label) {
      label.textContent = isIn ? 'Remove from Wishlist' : 'Save to Wishlist';
    }
  }

  // Listen for wishlist updates from global wishlist.js to sync PDP tooltips
  window.addEventListener('wishlist:updated', function(e) {
    const section = document.querySelector('[data-product-section]');
    if (!section) return;
    section.querySelectorAll('[data-wishlist-btn], [data-wishlist-btn-secondary]').forEach((button) => {
      const productId = button.dataset.productId;
      if (productId) {
        updateWishlistButtonState(button, productId);
      }
    });
  });

  function initProductSection(section) {
    const form = section.querySelector('[data-product-form]');
    const productDataNode = section.querySelector('[data-product-json]');
    const productHandleNode = section.querySelector('[data-product-handle]');
    const galleryGroupsNode = section.querySelector('[data-product-gallery-groups]');

    if (!form || !productDataNode) {
      return;
    }

    let variants;
    let productHandle = null;
    let galleryGroups = {};
    let galleryGroupColorOptionIndex = -1;

    try {
      variants = JSON.parse(productDataNode.textContent);
    } catch (error) {
      return;
    }

    if (!Array.isArray(variants) || variants.length === 0) {
      return;
    }

    if (productHandleNode) {
      try {
        productHandle = JSON.parse(productHandleNode.textContent);
      } catch (e) {
        productHandle = null;
      }
    }

    if (galleryGroupsNode) {
      try {
        const parsedGalleryGroups = JSON.parse(galleryGroupsNode.textContent);
        if (parsedGalleryGroups && typeof parsedGalleryGroups === 'object') {
          galleryGroups = parsedGalleryGroups.groups || {};
          galleryGroupColorOptionIndex = Number(parsedGalleryGroups.colorOptionIndex);
        }
      } catch (e) {
        galleryGroups = {};
        galleryGroupColorOptionIndex = -1;
      }
    }

    const variantInput = form.querySelector('input[name="id"]');
    if (!variantInput) {
      return;
    }

    const locale = document.documentElement.lang || 'en-US';
    const currency = section.dataset.currency || 'USD';
    const formatter = moneyFormatter(locale, currency);
    const useServerOptionNavigation = section.dataset.useServerOptionNavigation === 'true';
    const variantsLoadedCount = Number(section.dataset.variantsLoadedCount || variants.length || 0);
    const variantsTotalCount = Number(section.dataset.variantsTotalCount || variantsLoadedCount || variants.length || 0);
    const sectionId = section.dataset.sectionId || '';
    let sectionRenderController = null;
    // Shopify Liquid caps product.variants at 250 entries. When the loaded
    // count is exactly 250 we cannot tell whether the data is complete or
    // truncated. Treat it as potentially incomplete so fetchFullProductData
    // runs before any variant lookups.
    const VARIANT_LIMIT = 250;
    let hasCompleteVariantData =
      variantsLoadedCount < VARIANT_LIMIT && variantsLoadedCount === variantsTotalCount;
    let fullProductDataPromise = null;
    let selectionRequestId = 0;

    const gallery = initGallery(section);
    initVariantTabs(section);
    initStickyAtc(section, form);
    updateWishlistButtons(section);

    // Find the color option index by checking which panel has color-opts-grid
    let colorOptionIndex = -1;
    const colorPanel = section.querySelector('.color-opts-grid[data-option-panel]');
    if (colorPanel) {
      colorOptionIndex = Number(colorPanel.dataset.optionPanel);
    }

    const priceEls = Array.from(section.querySelectorAll('[data-price]'));
    const compareWrapEls = Array.from(section.querySelectorAll('[data-compare-wrap]'));
    const compareEls = Array.from(section.querySelectorAll('[data-compare-price]'));
    const saveEls = Array.from(section.querySelectorAll('[data-price-save]'));
    const stickyPriceEl = section.querySelector('[data-sticky-price]');
    const stickyVariantEl = section.querySelector('[data-sticky-variant]');
    const stickySubmitButton = section.querySelector('[data-sticky-atc-submit]');
    const addToCartButton = section.querySelector('[data-add-to-cart]');
    const addToCartMain = section.querySelector('[data-atc-main]');

    const addLabel = section.dataset.addLabel || 'Add to cart';
    const soldOutLabel = section.dataset.soldoutLabel || 'Sold out';
    const productIdSeed = Number(section.dataset.productId || 0);
    const deliveryDateFormatter = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
    let isBundledSubmitInFlight = false;
    let storageBundle = null;
    let serviceBundle = null;
    let trackedBasePrice = 0;
    let trackedBaseComparePrice = 0;

    function getGalleryMediaIdsForVariant(variant) {
      if (
        !variant ||
        !Array.isArray(variant.options) ||
        !Number.isFinite(galleryGroupColorOptionIndex) ||
        galleryGroupColorOptionIndex < 0
      ) {
        return [];
      }

      const colorValue = String(variant.options[galleryGroupColorOptionIndex] || '').trim();
      if (!colorValue || !galleryGroups || typeof galleryGroups !== 'object') {
        return [];
      }

      return Array.isArray(galleryGroups[colorValue]) ? galleryGroups[colorValue] : [];
    }

    function hashSeed(value) {
      const input = String(value || '');
      let hash = 2166136261;

      for (let i = 0; i < input.length; i += 1) {
        hash ^= input.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
      }

      return hash >>> 0;
    }

    function seededInt(seed, min, max) {
      const lower = Math.ceil(min);
      const upper = Math.floor(max);

      if (!Number.isFinite(lower) || !Number.isFinite(upper) || upper < lower) {
        return lower;
      }

      const span = upper - lower + 1;
      return lower + (hashSeed(seed) % span);
    }

    function getCurrentSizeLabel() {
      const tabs = section.querySelectorAll('[data-variant-tab]');

      for (const tab of tabs) {
        const labelNode = tab.querySelector('.variant-tab-label');
        const valueNode = tab.querySelector('[data-tab-current][data-option-index]');
        const label = (labelNode ? labelNode.textContent : '').trim().toLowerCase();

        if (!label.includes('size') || !valueNode) {
          continue;
        }

        const value = (valueNode.textContent || '').trim();
        if (value) {
          return value;
        }
      }

      return 'the selected size';
    }

    function addBusinessDays(fromDate, businessDays) {
      const date = new Date(fromDate.getTime());
      let remaining = Math.max(0, Number(businessDays) || 0);

      while (remaining > 0) {
        date.setDate(date.getDate() + 1);
        const day = date.getDay();
        if (day !== 0 && day !== 6) {
          remaining -= 1;
        }
      }

      return date;
    }

    function buildCroState(variant) {
      const bucket = Math.floor(Date.now() / (2 * 60 * 60 * 1000));
      const sizeLabel = getCurrentSizeLabel();
      const variantPart = variant && variant.id ? String(variant.id) : sizeLabel;
      const baseKey = `${productIdSeed || section.dataset.productTitle || 'product'}|${variantPart}|${bucket}`;

      const hoursWindow = seededInt(`${baseKey}|hours`, 18, 36);
      const orders = seededInt(`${baseKey}|orders`, 6, 17);
      const stockRemaining = seededInt(`${baseKey}|stockRemaining`, 2, 7);
      const stockTotal = stockRemaining + seededInt(`${baseKey}|stockBuffer`, 5, 11);
      const soldUnits = Math.max(1, stockTotal - stockRemaining);
      const soldPercent = Math.min(92, Math.max(20, Math.round((soldUnits / stockTotal) * 100)));
      const businessMinDays = 3;
      const businessMaxDays = 5;
      const startDate = addBusinessDays(new Date(), businessMinDays);
      const endDate = addBusinessDays(new Date(), businessMaxDays);
      const minutesAgo = seededInt(`${baseKey}|minutesAgo`, 4, 38);

      return {
        sizeLabel,
        orders,
        hoursWindow,
        stockRemaining,
        stockTotal,
        soldPercent,
        businessMinDays,
        businessMaxDays,
        startDateLabel: deliveryDateFormatter.format(startDate),
        endDateLabel: deliveryDateFormatter.format(endDate),
        minutesAgo,
      };
    }

    function updateCroUi(variant) {
      const state = buildCroState(variant);

      section.querySelectorAll('[data-cro-demand-title]').forEach((node) => {
        node.textContent = `${state.orders} UK orders in the last ${state.hoursWindow} hours`;
      });

      section.querySelectorAll('[data-cro-demand-sub]').forEach((node) => {
        node.textContent = `Updated ${state.minutesAgo} minutes ago`;
      });

      section.querySelectorAll('[data-cro-size-label]').forEach((node) => {
        node.textContent = state.sizeLabel;
      });

      section.querySelectorAll('[data-cro-stock-left]').forEach((node) => {
        node.textContent = String(state.stockRemaining);
      });

      section.querySelectorAll('[data-cro-stock-bar-count]').forEach((node) => {
        node.textContent = `Only ${state.stockRemaining} of ${state.stockTotal} remaining`;
      });

      section.querySelectorAll('[data-cro-stock-fill]').forEach((node) => {
        node.style.width = `${state.soldPercent}%`;
      });

      section.querySelectorAll('[data-cro-delivery-days]').forEach((node) => {
        node.textContent = `${state.businessMinDays}-${state.businessMaxDays} working days`;
      });

      section.querySelectorAll('[data-cro-delivery-dates]').forEach((node) => {
        node.textContent = `${state.startDateLabel} - ${state.endDateLabel}`;
      });
    }

    function updateBrowserUrl(variantId, sourceUrl = '') {
      try {
        const nextUrl = sourceUrl ? new URL(sourceUrl, window.location.origin) : new URL(window.location.href);

        if (variantId) {
          nextUrl.searchParams.set('variant', String(variantId));
        }

        const historyUrl = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
        window.history.replaceState({}, '', historyUrl);
      } catch (error) {
        // Ignore URL sync issues in unsupported environments.
      }
    }

    function emitVariantChange(variant) {
      const detail = {
        sectionId,
        variant,
      };

      section.dispatchEvent(
        new CustomEvent('variant:change', {
          bubbles: true,
          detail,
        })
      );

      document.dispatchEvent(
        new CustomEvent('variant:change', {
          detail,
        })
      );
    }

    function getRenderedSectionFromMarkup(markup) {
      if (!markup) {
        return null;
      }

      const template = document.createElement('template');
      template.innerHTML = String(markup).trim();

      const renderedSection = template.content.querySelector('[data-product-section]');
      if (renderedSection) {
        return renderedSection;
      }

      if (
        template.content.firstElementChild &&
        template.content.firstElementChild.matches('[data-product-section]')
      ) {
        return template.content.firstElementChild;
      }

      return null;
    }

    function reinitializeRenderedProductSection(nextSection) {
      initProductSection(nextSection);
      initInfoTabs(nextSection);
      initDimensionsTabs(nextSection);
      initFaq(nextSection);
      initChat(nextSection);
      initPageAnchors(nextSection);
      initRevealAnimation(nextSection);
      initFabricTabs(nextSection);
    }

    function restoreActiveVariantTab(nextSection, optionIndex, preferredTabLabel = '') {
      if (!nextSection) {
        return;
      }

      const normalizeLabel = (value) => String(value || '').trim().toLowerCase();
      const tabs = Array.from(nextSection.querySelectorAll('[data-variant-tab]'));
      const preferredLabel = normalizeLabel(preferredTabLabel);

      if (preferredLabel) {
        const preferredTab = tabs.find((tab) => {
          const label = normalizeLabel(tab.querySelector('.variant-tab-label')?.textContent);
          return label === preferredLabel;
        });

        if (preferredTab) {
          preferredTab.click();
          return;
        }
      }

      if (!Number.isFinite(optionIndex)) {
        return;
      }

      const tabValueNode = nextSection.querySelector(
        `[data-tab-current][data-option-index="${optionIndex}"]`
      );
      const targetTab = tabValueNode ? tabValueNode.closest('[data-variant-tab]') : null;

      if (targetTab) {
        targetTab.click();
      }
    }

    async function renderSectionForSelection(sourceUrl, changedOptionIndex = undefined) {
      if (!sourceUrl) {
        return false;
      }

      if (!sectionId) {
        window.location.assign(sourceUrl);
        return false;
      }

      if (sectionRenderController) {
        sectionRenderController.abort();
      }

      section.classList.add('is-variant-loading');
      section.setAttribute('aria-busy', 'true');

      const controller = new AbortController();
      sectionRenderController = controller;

      try {
        const targetUrl = new URL(sourceUrl, window.location.origin);
        const requestUrl = new URL(targetUrl.toString());
        requestUrl.searchParams.set('sections', sectionId);

        let sectionMarkup = '';

        // Always try the sections API first, even with option_values.
        // Shopify's section rendering API processes option_values to render
        // the section with the correct variant selected.
        const sectionsResponse = await fetch(requestUrl.toString(), {
          signal: controller.signal,
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (sectionsResponse.ok) {
          const contentType = sectionsResponse.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const payload = await sectionsResponse.json();
            if (payload && typeof payload === 'object') {
              sectionMarkup = payload[sectionId] || '';
            }
          } else {
            sectionMarkup = await sectionsResponse.text();
            if (sectionMarkup.trim().startsWith('{')) {
              try {
                const payload = JSON.parse(sectionMarkup);
                if (payload && typeof payload === 'object') {
                  sectionMarkup = payload[sectionId] || '';
                }
              } catch (error) {
                // Keep the text response as-is if it's not valid JSON.
              }
            }
          }
        }

        if (!sectionMarkup) {
          const fallbackResponse = await fetch(targetUrl.toString(), {
            signal: controller.signal,
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
            },
          });

          if (fallbackResponse.ok) {
            sectionMarkup = await fallbackResponse.text();
          }
        }

        if (controller.signal.aborted) {
          return false;
        }

        const nextSection = getRenderedSectionFromMarkup(sectionMarkup);
        if (!nextSection) {
          window.location.assign(sourceUrl);
          return false;
        }

        updateBrowserUrl(null, sourceUrl);

        // Preserve custom option selections before replacing section
        const preservedSelections = {
          storageOption: null,
          buttonOption: null,
          activeVariantTab: '',
        };

        const activeVariantTab = section.querySelector('[data-variant-tab].is-active, [data-variant-tab][aria-selected="true"]');
        if (activeVariantTab) {
          preservedSelections.activeVariantTab =
            activeVariantTab.querySelector('.variant-tab-label')?.textContent || '';
        }

        // Preserve storage option selection
        const selectedStorageBtn = section.querySelector('[data-storage-option].is-selected, [data-storage-option][aria-pressed="true"]');
        if (selectedStorageBtn) {
          preservedSelections.storageOption = selectedStorageBtn.dataset.storageLabel || selectedStorageBtn.dataset.optionValue || '';
        }

        // Preserve button option selection
        const selectedButtonBtn = section.querySelector('[data-button-option-choice].is-selected, [data-button-option-choice][aria-pressed="true"]');
        if (selectedButtonBtn) {
          preservedSelections.buttonOption = selectedButtonBtn.dataset.buttonOptionLabel || '';
        }

        // Preserve scroll position before replacing section
        const scrollY = window.scrollY;
        const sectionRect = section.getBoundingClientRect();
        const sectionTop = scrollY + sectionRect.top;

        section.replaceWith(nextSection);
        reinitializeRenderedProductSection(nextSection);
        restoreActiveVariantTab(nextSection, changedOptionIndex, preservedSelections.activeVariantTab);

        // Restore custom option selections after reinitialization
        if (preservedSelections.storageOption) {
          const storageBtnToRestore = nextSection.querySelector(
            `[data-storage-option][data-storage-label="${preservedSelections.storageOption}"], [data-storage-option][data-option-value="${preservedSelections.storageOption}"]`
          );
          if (storageBtnToRestore && storageBtnToRestore.style.display !== 'none') {
            storageBtnToRestore.click();
          }
        }

        if (preservedSelections.buttonOption) {
          const buttonBtnToRestore = nextSection.querySelector(
            `[data-button-option-choice][data-button-option-label="${preservedSelections.buttonOption}"]`
          );
          if (buttonBtnToRestore) {
            buttonBtnToRestore.click();
          }
        }

        // Restore scroll position if section was in view
        const newSectionRect = nextSection.getBoundingClientRect();
        if (sectionRect.top < window.innerHeight && sectionRect.bottom > 0) {
          window.scrollTo(0, scrollY);
        }

        return true;
      } catch (error) {
        if (error && error.name === 'AbortError') {
          return false;
        }

        console.warn('Could not render product section for selected option:', error);
        window.location.assign(sourceUrl);
        return false;
      } finally {
        if (!controller.signal.aborted) {
          section.classList.remove('is-variant-loading');
          section.removeAttribute('aria-busy');
        }
      }
    }

    function hasSelectedOptionValues(values) {
      return Array.isArray(values) && values.some((value) => String(value || '').trim() !== '');
    }

    function isVisibleOptionButton(button) {
      if (!(button instanceof HTMLElement)) {
        return false;
      }

      if (button.hidden || button.style.display === 'none') {
        return false;
      }

      const fabricPanel = button.closest('[data-fabric-panel]');
      if (fabricPanel && !fabricPanel.classList.contains('is-active')) {
        return false;
      }

      return true;
    }

    function getSelectedOptionValuesByIndex({ visibleOnly = false } = {}) {
      const selectedValues = [];

      section
        .querySelectorAll('[data-option-index][data-option-value]')
        .forEach((button) => {
          const optionIndex = Number(button.dataset.optionIndex);
          const optionValue = String(button.dataset.optionValue || '').trim();
          const isSelected =
            button.classList.contains('is-selected') ||
            button.getAttribute('aria-pressed') === 'true';

          if (visibleOnly && !isVisibleOptionButton(button)) {
            return;
          }

          if (!Number.isFinite(optionIndex) || !optionValue || !isSelected) {
            return;
          }

          selectedValues[optionIndex] = optionValue;
        });

      return selectedValues;
    }

    const currentVariantId = Number(variantInput.value);
    const currentVariant = variants.find((variant) => variant.id === currentVariantId) || null;
    const isServerVariantOutsideClientData = useServerOptionNavigation && !currentVariant;
    const urlHasOptionValues = (() => {
      try {
        return new URL(window.location.href).searchParams.has('option_values');
      } catch (error) {
        return false;
      }
    })();
    const normalizeInitialOptionValue = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
    const initialOptionValuesMatch = (a, b) => normalizeInitialOptionValue(a) === normalizeInitialOptionValue(b);
    const selectedOptionsFromMarkup = getSelectedOptionValuesByIndex();
    const hasSelectedOptionsInMarkup = selectedOptionsFromMarkup.some(
      (optionValue) => String(optionValue || '').trim() !== ''
    );
    const baseInitialOptions = currentVariant && Array.isArray(currentVariant.options)
      ? currentVariant.options.slice()
      : [];
    const markupSelectedOptions = baseInitialOptions.slice();

    if (hasSelectedOptionsInMarkup) {
      selectedOptionsFromMarkup.forEach((optionValue, index) => {
        if (String(optionValue || '').trim() !== '') {
          markupSelectedOptions[index] = optionValue;
        }
      });
    }

    const markupSelectionDiffersFromCurrentVariant = Boolean(
      hasSelectedOptionsInMarkup &&
      currentVariant &&
      Array.isArray(currentVariant.options) &&
      markupSelectedOptions.some((optionValue, index) => (
        String(optionValue || '').trim() !== '' &&
        !initialOptionValuesMatch(optionValue, currentVariant.options[index])
      ))
    );
    const markupSelectedVariant = hasSelectedOptionsInMarkup
      ? variants.find((variant) => (
        Array.isArray(variant.options) &&
        variant.options.every((optionValue, index) => initialOptionValuesMatch(optionValue, markupSelectedOptions[index]))
      ))
      : null;

    let initialVariant =
      markupSelectedVariant ||
      currentVariant ||
      variants.find((variant) => variant.available) ||
      variants[0];

    let selectedOptions = Array.isArray(initialVariant.options)
      ? initialVariant.options.slice()
      : [];

    if (
      hasSelectedOptionsInMarkup &&
      (isServerVariantOutsideClientData || urlHasOptionValues || markupSelectionDiffersFromCurrentVariant)
    ) {
      selectedOptions = markupSelectedOptions;
    }
    const shouldDeferInitialVariantUi = Boolean(
      markupSelectionDiffersFromCurrentVariant &&
      !markupSelectedVariant &&
      productHandle
    );

    let pendingSelectionSync = Promise.resolve();

    function shouldUseServerOptionNavigation() {
      return useServerOptionNavigation && !hasCompleteVariantData;
    }

    function shouldRenderSelectionFromServer(optionUrl = '') {
      if (!useServerOptionNavigation || !optionUrl) {
        return false;
      }

      try {
        return new URL(optionUrl, window.location.origin).searchParams.has('option_values');
      } catch (error) {
        return false;
      }
    }

    // For truncated products or products near the limit, load complete data eagerly.
    if (
      productHandle &&
      (shouldUseServerOptionNavigation() || variants.length >= VARIANT_LIMIT || markupSelectionDiffersFromCurrentVariant)
    ) {
      fetchFullProductData(productHandle).then(() => {
        // If the user has already clicked an option, a syncToSelection call
        // is in progress (or queued). Do NOT overwrite selectedOptions from
        // the DOM here — syncToSelection owns the state and will update the
        // UI itself once the shared fetch promise resolves.
        if (selectionRequestId > 0) {
          return;
        }

        // After loading full data, re-read the user's selections from the DOM
        // (the Liquid template may have set options that don't map to any
        // variant in the truncated 250-item set) and update everything.
        syncSelectedOptionsFromUi();
        updateOptionButtons();
        updateTabLabels();
        updateFabricTabs();

        // Find the correct variant for the user's selections and update
        // the price, variant input, gallery, and URL. Without this the
        // price stays at the first variant's value (£299.99) even though
        // the UI shows a different colour/size/headboard.
        const correctVariant = findExactVariant();
        if (correctVariant) {
          updateVariantUi(correctVariant);
          updateBrowserUrl(correctVariant.id);
        }
      });
    }

    function normalizeVariantOptions(variantList) {
      if (!Array.isArray(variantList)) {
        return variantList;
      }

      for (const variant of variantList) {
        if (!variant || Array.isArray(variant.options)) {
          continue;
        }

        const opts = [];
        if (variant.option1 != null && variant.option1 !== '') {
          opts.push(String(variant.option1));
        }
        if (variant.option2 != null && variant.option2 !== '') {
          opts.push(String(variant.option2));
        }
        if (variant.option3 != null && variant.option3 !== '') {
          opts.push(String(variant.option3));
        }

        if (opts.length > 0) {
          variant.options = opts;
        }
      }

      return variantList;
    }

    async function fetchFullProductData(handle) {
      if (!handle) {
        return false;
      }

      if (hasCompleteVariantData) {
        return true;
      }

      if (fullProductDataPromise) {
        return fullProductDataPromise;
      }

      fullProductDataPromise = (async () => {
        try {
          const response = await fetch(`/products/${handle}.js`);
          if (!response.ok) {
            return false;
          }

          const product = await response.json();
          if (Array.isArray(product.variants) && product.variants.length > 0) {
            normalizeVariantOptions(product.variants);
            // Only mark data as complete when the API returns strictly MORE
            // variants than the Liquid template's 250-item cap, or when we
            // never hit the cap. If the API also returns <= 250 variants the
            // data may still be truncated, so keep server navigation active.
            const apiExceedsLiquidCap = product.variants.length > VARIANT_LIMIT;
            const apiMatchesLiquidCount = product.variants.length >= variants.length;
            if (apiExceedsLiquidCap || (apiMatchesLiquidCount && variants.length < VARIANT_LIMIT)) {
              variants = product.variants;
              hasCompleteVariantData = true;
            } else if (apiMatchesLiquidCount) {
              // API returned >= our current count but didn't exceed the cap.
              // Replace data anyway but keep hasCompleteVariantData false so
              // server navigation stays active as a safety net.
              variants = product.variants;
            }

            const activeVariantId = Number(variantInput.value || currentVariantId || 0);
            const activeVariant =
              variants.find((variant) => variant.id === activeVariantId) ||
              variants.find((variant) => variant.available) ||
              variants[0];

            if (activeVariant) {
              initialVariant = activeVariant;
            }

            if ((!Array.isArray(selectedOptions) || selectedOptions.length === 0) && activeVariant && Array.isArray(activeVariant.options)) {
              selectedOptions = activeVariant.options.slice();
            }

            return hasCompleteVariantData;
          }

          return false;
        } catch (error) {
          // Silently fail - fallback to embedded data
          console.warn('Could not load full product data:', error);
          return false;
        } finally {
          fullProductDataPromise = null;
        }
      })();

      return fullProductDataPromise;
    }

    function normalizeOptionValue(value) {
      return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
    }

    function optionValuesMatch(a, b) {
      return normalizeOptionValue(a) === normalizeOptionValue(b);
    }

    function findExactVariantForOptions(optionValues) {
      return variants.find((variant) => {
        if (!Array.isArray(variant.options)) {
          return false;
        }

        // Both arrays must have the same number of option dimensions to be an
        // exact match.  Without this guard a variant that only has 2 populated
        // options could silently match a 3-option selection because
        // Array.prototype.every stops at the shorter length.
        const requestedCount = optionValues.filter((v) => String(v || '').trim() !== '').length;
        if (variant.options.length !== requestedCount) {
          return false;
        }

        return variant.options.every((optionValue, index) => optionValuesMatch(optionValue, optionValues[index]));
      });
    }

    function findExactVariant() {
      return findExactVariantForOptions(selectedOptions);
    }

    function findClosestAvailableForOptions(optionValues) {
      // Build a score to prefer variants that match more of the requested options.
      // This prevents returning a variant with a wildly different size when one
      // option index is missing from optionValues.
      let bestVariant = null;
      let bestScore = -1;

      for (const variant of variants) {
        if (!variant.available || !Array.isArray(variant.options)) {
          continue;
        }

        let matchCount = 0;
        let allSetMatch = true;

        for (let index = 0; index < variant.options.length; index++) {
          const requestedValue = optionValues[index];
          if (String(requestedValue || '').trim() === '') {
            // No preference for this option – count it as a neutral match.
            continue;
          }
          if (optionValuesMatch(requestedValue, variant.options[index])) {
            matchCount++;
          } else {
            allSetMatch = false;
            break;
          }
        }

        if (!allSetMatch) {
          continue;
        }

        if (matchCount > bestScore) {
          bestScore = matchCount;
          bestVariant = variant;
        }
      }

      return bestVariant;
    }

    function findClosestAvailable(optionIndex, optionValue) {
      let bestMatch = null;
      let bestScore = -1;

      for (const variant of variants) {
        if (!variant.available || !Array.isArray(variant.options)) {
          continue;
        }

        let matches = 0;
        let valid = true;

        for (let index = 0; index < variant.options.length; index++) {
          const variantOption = variant.options[index];
          if (index === optionIndex) {
            if (!optionValuesMatch(variantOption, optionValue)) {
              valid = false;
              break;
            }
            matches++;
          } else if (selectedOptions[index]) {
            if (!optionValuesMatch(selectedOptions[index], variantOption)) {
              valid = false;
              break;
            }
            matches++;
          }
        }

        if (valid && matches > bestScore) {
          bestScore = matches;
          bestMatch = variant;
        }
      }

      return bestMatch;
    }

    function syncSelectedOptionsFromUi({ visibleOnly = false } = {}) {
      const selectedValuesFromUi = getSelectedOptionValuesByIndex({ visibleOnly });
      if (hasSelectedOptionValues(selectedValuesFromUi)) {
        // Merge UI values into existing selectedOptions instead of replacing.
        // This prevents losing selections for option indices that have no
        // is-selected button in the DOM (e.g. after a fabric tab switch or
        // partial DOM update), which would make selectedOptions sparse and
        // cause findClosestAvailableForOptions to match wrong variants.
        for (let i = 0; i < selectedValuesFromUi.length; i++) {
          if (String(selectedValuesFromUi[i] || '').trim() !== '') {
            selectedOptions[i] = selectedValuesFromUi[i];
          }
        }
      }

      return Array.isArray(selectedOptions) ? selectedOptions.slice() : [];
    }

    function trackSelectionSync(task) {
      pendingSelectionSync = Promise.resolve(task).catch((error) => {
        console.warn('Could not finish syncing the selected variant:', error);
        return false;
      });

      return pendingSelectionSync;
    }

    async function resolveVariantForSubmission() {
      await pendingSelectionSync;

      syncSelectedOptionsFromUi();

      // The hidden variant input may already hold the correct ID set by
      // server-rendered HTML (after an option_values section re-render).
      // For products with >250 variants the AJAX API is also capped at 250,
      // so fetching "full" data never actually helps — it just adds latency
      // and the client-side lookups below will still fail for variants beyond
      // the cap.  Skip the fetch when we already know data is truncated.
      const activeVariantId = String(variantInput.value || '').trim();

      if (productHandle && !hasCompleteVariantData) {
        await fetchFullProductData(productHandle);
        syncSelectedOptionsFromUi();
      }

      // Ensure selectedOptions is fully populated. If any option index is
      // missing, fill it from the current variant input so that variant
      // lookups never match on incomplete criteria.
      if (Array.isArray(selectedOptions)) {
        const currentFallback = variants.find((item) => String(item.id) === activeVariantId);
        if (currentFallback && Array.isArray(currentFallback.options)) {
          for (let i = 0; i < currentFallback.options.length; i++) {
            if (String(selectedOptions[i] || '').trim() === '') {
              selectedOptions[i] = currentFallback.options[i];
            }
          }
        }
      }

      let variant =
        findExactVariantForOptions(selectedOptions) ||
        findClosestAvailableForOptions(selectedOptions);

      if (!variant && activeVariantId) {
        variant = variants.find((item) => String(item.id) === activeVariantId) || null;
      }

      // For truncated products (>250 variants) the variant may not exist in
      // the client-side data at all, even after the AJAX fetch.  In that case
      // the variantInput.value was set by server rendering and is authoritative.
      // Build a minimal variant object so the form submits the correct ID.
      if (!variant && activeVariantId && useServerOptionNavigation) {
        return { id: Number(activeVariantId) };
      }

      if (!variant) {
        return null;
      }

      if (Array.isArray(variant.options)) {
        selectedOptions = variant.options.slice();
        updateOptionButtons();
        updateTabLabels();
        updateFabricTabs();
      }

      updateVariantUi(variant);
      updateBrowserUrl(variant.id);

      return variant;
    }

    function getSubmitTargetForm() {
      if (form.isConnected) {
        return form;
      }

      if (sectionId) {
        const liveForm = document.querySelector(
          `[data-product-section][data-section-id="${sectionId}"] [data-product-form]`
        );

        if (liveForm instanceof HTMLFormElement) {
          return liveForm;
        }
      }

      return form;
    }

    function updateOptionButtons() {
      section
        .querySelectorAll('[data-option-index][data-option-value]')
        .forEach((button) => {
          const optionIndex = Number(button.dataset.optionIndex);
          const optionValue = button.dataset.optionValue;
          const isSelected = optionValuesMatch(selectedOptions[optionIndex], optionValue);

          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-pressed', String(isSelected));

          if (shouldUseServerOptionNavigation()) {
            return;
          }

          const matchingVariant = variants.find((variant) => {
            return variant.options.every((variantOption, index) => {
              if (index === optionIndex) {
                return optionValuesMatch(variantOption, optionValue);
              }

              return !selectedOptions[index] || optionValuesMatch(selectedOptions[index], variantOption);
            });
          });

          if (matchingVariant) {
            button.dataset.optionPrice = String(Number(matchingVariant.price || 0));
          } else {
            button.dataset.optionPrice = '';
          }

          const availableForState = variants.some((variant) => {
            if (!variant.available) {
              return false;
            }

            return variant.options.every((variantOption, index) => {
              if (index === optionIndex) {
                return optionValuesMatch(variantOption, optionValue);
              }

              return !selectedOptions[index] || optionValuesMatch(selectedOptions[index], variantOption);
            });
          });

          button.disabled = !availableForState;
          button.classList.toggle('is-unavailable', !availableForState);
        });
    }

    function updateTabLabels() {
      section.querySelectorAll('[data-tab-current]').forEach((label) => {
        const optionIndex = Number(label.dataset.optionIndex);
        const nextLabel = selectedOptions[optionIndex] || 'Select option';
        label.textContent = nextLabel;
        label.setAttribute('title', nextLabel);
      });

      updateVariantTabIcons();

      if (colorOptionIndex >= 0) {
        const currentColor = selectedOptions[colorOptionIndex] || 'Select option';
        section.querySelectorAll('[data-product-color-current]').forEach((label) => {
          label.textContent = currentColor;
          label.setAttribute('title', currentColor);
        });
      }
    }

    function updateVariantTabIcons() {
      section.querySelectorAll('[data-tab-current][data-option-index]').forEach((label) => {
        const optionIndex = Number(label.dataset.optionIndex);
        const tab = label.closest('[data-variant-tab]');
        const tabIcon = tab ? tab.querySelector('.variant-tab-icon') : null;
        const tabLabel = (tab?.querySelector('.variant-tab-label')?.textContent || '').trim().toLowerCase();

        if (
          !tab ||
          !tabIcon ||
          !Number.isFinite(optionIndex) ||
          !(
            tabLabel.includes('storage') ||
            tabLabel.includes('drawer') ||
            tabLabel.includes('ottoman') ||
            tabLabel.includes('headboard') ||
            tabLabel.includes('height') ||
            tabLabel.includes('hight')
          )
        ) {
          return;
        }

        const selectedButton = section.querySelector(
          `[data-option-index="${optionIndex}"][data-option-value].is-selected, ` +
          `[data-option-index="${optionIndex}"][data-option-value][aria-pressed="true"]`
        );
        const selectedIcon = selectedButton?.querySelector('.storage-opt-icon, .headboard-opt-icon, .opt-card-icon');

        if (!selectedIcon) {
          return;
        }

        tabIcon.innerHTML = selectedIcon.innerHTML;
        tabIcon.classList.toggle('variant-tab-icon--selected-art', Boolean(tabIcon.querySelector('img')));
      });
    }

    function toHandle(value) {
      return String(value || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    function resolveFabricHandleFromColorLabel(colorLabel) {
      const selectedColor = String(colorLabel || '').trim();

      if (!selectedColor) {
        return '';
      }

      const parts = selectedColor.split(/\s+/).filter(Boolean);
      if (parts.length === 0) {
        return '';
      }

      const lastWord = parts[parts.length - 1];
      if (lastWord.toLowerCase() === 'velvet' && parts.length > 1) {
        return toHandle(`${parts[parts.length - 2]} ${lastWord}`);
      }

      return toHandle(lastWord);
    }

    function updateFabricTabs() {
      const fabricTabsContainer = section.querySelector('[data-fabric-tabs]');
      if (!fabricTabsContainer) return;
      if (colorOptionIndex < 0) return;

      const tabs = fabricTabsContainer.querySelectorAll('[data-fabric-tab]');
      const grids = section.querySelectorAll('[data-fabric-panel]');

      // Find the fabric of the currently selected color option
      const selectedColor = selectedOptions[colorOptionIndex] || '';
      let selectedFabric = resolveFabricHandleFromColorLabel(selectedColor);
      let hasFabricMatch = false;

      // Update tabs
      tabs.forEach((tab) => {
        const isActive = tab.dataset.fabricTab === selectedFabric;
        if (isActive) {
          hasFabricMatch = true;
        }
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-pressed', String(isActive));
      });

      if (!hasFabricMatch && tabs.length > 0) {
        selectedFabric = tabs[0].dataset.fabricTab || '';
        tabs.forEach((tab, index) => {
          const isActive = index === 0;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-pressed', String(isActive));
        });
      }

      // Update grids
      grids.forEach((grid) => {
        grid.classList.toggle('is-active', grid.dataset.fabricPanel === selectedFabric);
      });
    }

    function updateOptionPriceDeltas(basePrice) {
      section.querySelectorAll('[data-option-price-delta]').forEach((label) => {
        const optionButton = label.closest('[data-option-value]');

        if (!optionButton) {
          return;
        }

        const isSelectedOption =
          optionButton.classList.contains('is-selected') ||
          optionButton.getAttribute('aria-pressed') === 'true';
        if (isSelectedOption) {
          label.textContent = 'Selected';
          label.classList.remove('is-plus', 'is-minus', 'is-zero');
          return;
        }

        // Check if this is a FREE option - always show 0 price delta for these
        const optionValue = optionButton.dataset.optionValue || '';
        if (optionValue.toUpperCase().includes('FREE')) {
          label.textContent = '';
          label.classList.remove('is-plus', 'is-minus', 'is-zero');
          return;
        }

        const optionPrice = Number(optionButton.dataset.optionPrice);
        if (!Number.isFinite(optionPrice)) {
          label.textContent = '';
          label.classList.remove('is-plus', 'is-minus', 'is-zero');
          return;
        }

        const delta = optionPrice - Number(basePrice || 0);
        // Only show positive price deltas.
        if (delta <= 0) {
          label.textContent = '';
          label.classList.remove('is-plus', 'is-minus', 'is-zero');
        } else {
          label.textContent = formatSignedMoney(formatter, delta);
          label.classList.toggle('is-plus', delta > 0);
          label.classList.remove('is-minus');
        }
      });
    }

    function getSelectedStorageAddonPrice() {
      if (!storageBundle || typeof storageBundle.getSelectedPrice !== 'function') {
        return 0;
      }

      return Number(storageBundle.getSelectedPrice() || 0);
    }

    function getSelectedStorageAddonComparePrice() {
      if (!storageBundle || typeof storageBundle.getSelectedComparePrice !== 'function') {
        return 0;
      }

      return Number(storageBundle.getSelectedComparePrice() || 0);
    }

    function getSelectedServiceAddonPrice() {
      if (!serviceBundle || typeof serviceBundle.getSelectedPrice !== 'function') {
        return 0;
      }

      return Number(serviceBundle.getSelectedPrice() || 0);
    }

    let addonPriceFetchToken = 0;

    async function updatePriceWithCurrentAddons() {
      const productUrl = section.dataset.productUrl || '';
      if (!productUrl || !sectionId) {
        applyAddonPriceDisplay(
          Number(section.dataset.vfixBasePrice || 0) || trackedBasePrice,
          Number(section.dataset.vfixBaseComparePrice || 0) || trackedBaseComparePrice
        );
        return;
      }

      const token = ++addonPriceFetchToken;
      const ids = getSelectedOptionValueIds();
      const optionValues = ids.filter(Boolean).join(',');
      if (!optionValues) return;

      const url = productUrl + '?sections=' + sectionId + '&option_values=' + optionValues;

      try {
        const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (token !== addonPriceFetchToken) return;
        if (!res.ok) throw new Error('fetch failed');
        const payload = await res.json();
        const html = (payload && typeof payload === 'object') ? payload[sectionId] || '' : '';
        if (!html) throw new Error('empty');
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const serverPriceEl = doc.querySelector('[data-price]');
        const serverCompareEl = doc.querySelector('[data-compare-price]');
        const serverCompareWrap = doc.querySelector('[data-compare-wrap]');

        let basePrice = 0;
        if (serverPriceEl) {
          const num = parseFloat(serverPriceEl.textContent.replace(/[^0-9.]/g, ''));
          if (!isNaN(num)) basePrice = Math.round(num * 100);
        }
        let baseComparePrice = 0;
        if (serverCompareEl) {
          const num = parseFloat(serverCompareEl.textContent.replace(/[^0-9.]/g, ''));
          if (!isNaN(num)) baseComparePrice = Math.round(num * 100);
        }
        if (serverCompareWrap && serverCompareWrap.hidden) {
          baseComparePrice = 0;
        }

        trackedBasePrice = basePrice;
        trackedBaseComparePrice = baseComparePrice;
        applyAddonPriceDisplay(basePrice, baseComparePrice);
      } catch (e) {
        if (token !== addonPriceFetchToken) return;
        applyAddonPriceDisplay(
          trackedBasePrice || Number(section.dataset.vfixBasePrice || 0),
          trackedBaseComparePrice || Number(section.dataset.vfixBaseComparePrice || 0)
        );
      }
    }

    function applyAddonPriceDisplay(basePrice, baseComparePrice) {
      const storageAddonPrice = getSelectedStorageAddonPrice();
      const storageAddonComparePrice = getSelectedStorageAddonComparePrice();
      const serviceAddonPrice = getSelectedServiceAddonPrice();
      const displayPrice = basePrice + storageAddonPrice + serviceAddonPrice;
      const displayCompareAt = baseComparePrice + storageAddonComparePrice;
      const hasDiscount = displayCompareAt > displayPrice;

      priceEls.forEach((priceEl) => {
        priceEl.textContent = formatMoney(formatter, displayPrice);
      });

      if (stickyPriceEl) {
        stickyPriceEl.textContent = formatMoney(formatter, displayPrice);
      }

      compareWrapEls.forEach((compareWrapEl) => {
        compareWrapEl.hidden = !hasDiscount;
      });

      compareEls.forEach((compareEl) => {
        compareEl.textContent = hasDiscount ? formatMoney(formatter, displayCompareAt) : '';
      });

      saveEls.forEach((saveEl) => {
        if (hasDiscount && displayCompareAt > 0) {
          const discountPercent = Math.round(((displayCompareAt - displayPrice) / displayCompareAt) * 100);
          saveEl.textContent = `Save ${discountPercent}%`;
        } else {
          saveEl.textContent = '';
        }
      });
    }

    function getSelectedOptionValueIds() {
      const result = [];
      const byIndex = {};
      section.querySelectorAll('[data-option-value-id][data-option-index]').forEach((btn) => {
        const idx = btn.dataset.optionIndex;
        if (!byIndex[idx]) byIndex[idx] = [];
        byIndex[idx].push(btn);
      });
      Object.keys(byIndex).forEach((idx) => {
        const buttons = byIndex[idx];
        if (idx === '0') {
          const activePanel = section.querySelector('[data-fabric-panel].is-active');
          if (activePanel) {
            const activeBtn = activePanel.querySelector(
              '[data-option-index="0"].is-selected[data-option-value-id], ' +
              '[data-option-index="0"][aria-pressed="true"][data-option-value-id]'
            );
            if (activeBtn) { result[Number(idx)] = activeBtn.dataset.optionValueId; return; }
          }
        }
        for (let i = 0; i < buttons.length; i++) {
          const btn = buttons[i];
          if (btn.classList.contains('is-selected') || btn.getAttribute('aria-pressed') === 'true') {
            if (idx === '0') {
              const panel = btn.closest('[data-fabric-panel]');
              if (panel && !panel.classList.contains('is-active')) continue;
            }
            result[Number(idx)] = btn.dataset.optionValueId;
            return;
          }
        }
      });
      return result;
    }

    function syncWhatsInBoxSummary() {
      const whatsInBox = document.querySelector('[data-whats-in-box]');
      if (!whatsInBox || !form) {
        return;
      }

      const includedList = whatsInBox.querySelector('[data-wib-included-list]');
      if (!includedList) {
        return;
      }

      const specsTable = document.querySelector('[data-specs-table]');

      function getSpecValue(label) {
        if (!specsTable || !label) {
          return '';
        }

        const normalizedLabel = String(label).trim().toLowerCase();
        const specRows = specsTable.querySelectorAll('[data-spec-row]');

        for (const row of specRows) {
          const rowLabel = String(row.querySelector('[data-spec-label]')?.textContent || '')
            .trim()
            .toLowerCase();

          if (rowLabel === normalizedLabel) {
            return String(row.querySelector('[data-spec-value]')?.textContent || '').trim();
          }
        }

        return '';
      }

      function updateStaticItem(slot, line) {
        if (!slot || !line) {
          return;
        }

        const marker = includedList.querySelector(`[data-wib-static-slot="${slot}"]`);
        const itemText = marker?.parentElement?.querySelector('[data-wib-item-copy]');

        if (itemText) {
          itemText.replaceChildren(createWhatsInBoxContent(line));
        }
      }

      function normalizeWhatsInBoxHeading(label) {
        const trimmedLabel = String(label || '').trim();

        if (!trimmedLabel) {
          return '';
        }

        const lowerLabel = trimmedLabel.toLowerCase();

        if (lowerLabel === 'headboard hight') {
          return 'Headboard Height';
        }

        if (lowerLabel === 'storage add-on' || lowerLabel === 'storage add on') {
          return 'Storage Add-on';
        }

        if (lowerLabel === 'button option') {
          return 'Button Option';
        }

        return trimmedLabel.replace(/\bhight\b/gi, 'Height');
      }

      function createWhatsInBoxContent(line) {
        const content = document.createElement('span');
        content.className = 'whats-in-box-copy';

        const separatorIndex = String(line).indexOf(':');

        if (separatorIndex === -1) {
          content.textContent = String(line).trim();
          return content;
        }

        const rawHeading = String(line).slice(0, separatorIndex).trim();
        const rawValue = String(line).slice(separatorIndex + 1).trim();
        const heading = document.createElement('span');
        const value = document.createElement('span');

        heading.className = 'whats-in-box-item-heading';
        heading.textContent = `${normalizeWhatsInBoxHeading(rawHeading)}:`;

        value.className = 'whats-in-box-item-value';
        value.textContent = rawValue;

        content.append(heading, value);
        return content;
      }

      const productTypeName = String(section.dataset.productTypeName || '').trim();
      const productCategoryName = String(section.dataset.productCategoryName || '').trim();
      const productCollectionTitle = String(section.dataset.productCollectionTitle || '').trim();
      const productDisplayType = String(section.dataset.productDisplayType || '').trim();
      const collectionDisplayValue =
        productTypeName || productCategoryName || productCollectionTitle || productDisplayType;
      const headboardSpec = getSpecValue('Headboard');
      const legsSpec = getSpecValue('Legs');

      if (collectionDisplayValue) {
        updateStaticItem('collection', `Type: ${collectionDisplayValue}`);
      }

      if (headboardSpec) {
        updateStaticItem('headboard', `Headboard: ${headboardSpec}`);
      }

      if (legsSpec) {
        updateStaticItem('legs', `Legs: ${legsSpec}`);
      }

      includedList.querySelectorAll('[data-wib-dynamic-item]').forEach((item) => item.remove());

      const dynamicItems = [];
      const productTitle = String(section.dataset.productTitle || '').trim();
      let selectedSize = '';
      let selectedColour = '';
      const extraVariantLines = [];

      function isSizeLabel(label) {
        return label.includes('size');
      }

      function isColourLabel(label) {
        return label.includes('colour') || label.includes('color');
      }

      section.querySelectorAll('[data-variant-tab]').forEach((tab) => {
        const label = String(tab.querySelector('.variant-tab-label')?.textContent || '').trim();
        const valueNode = tab.querySelector('[data-tab-current]');
        const value = String(valueNode?.textContent || '').trim();
        const normalizedLabel = label.toLowerCase();

        if (!label || !value) {
          return;
        }

        if (isSizeLabel(normalizedLabel)) {
          selectedSize = value;
          return;
        }

        if (isColourLabel(normalizedLabel)) {
          selectedColour = value;
          return;
        }

        extraVariantLines.push(`${normalizeWhatsInBoxHeading(label)}: ${value}`);
      });

      if (productTitle) {
        const bedSummary = ['Detail: ' + productTitle];

        if (selectedSize) {
          bedSummary.push(selectedSize);
        }

        if (selectedColour) {
          bedSummary.push(selectedColour);
        }

        dynamicItems.push(bedSummary.join(', '));
      }

      dynamicItems.push(...extraVariantLines);

      const storageTitle = String(
        form.querySelector('input[name="properties[Storage Add-on]"]')?.value || ''
      ).trim();
      if (storageTitle) {
        dynamicItems.push(`Storage Add-on: ${storageTitle}`);
      }

      const buttonOption = String(
        form.querySelector('input[name="properties[Button Option]"]')?.value || ''
      ).trim();
      if (buttonOption) {
        dynamicItems.push(`Button Option: ${buttonOption}`);
      }

      const mattressTitle = String(
        form.querySelector('input[name="properties[Mattress Add-on]"]')?.value || ''
      ).trim();
      if (mattressTitle) {
        dynamicItems.push(`Mattress: ${mattressTitle}`);
      }

      const serviceTitles = String(
        form.querySelector('input[name="properties[Service Add-ons]"]')?.value || ''
      )
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

      serviceTitles.forEach((serviceTitle) => {
        dynamicItems.push(`Optional service: ${serviceTitle}`);
      });

      dynamicItems
        .reverse()
        .forEach((line) => {
          const item = document.createElement('li');
          const icon = document.createElement('span');
          const text = document.createElement('span');

          item.className = 'whats-in-box-item whats-in-box-item--dynamic';
          item.setAttribute('data-wib-dynamic-item', '');

          icon.className = 'mi';
          icon.setAttribute('aria-hidden', 'true');
          icon.textContent = 'check_circle';

          text.setAttribute('data-wib-item-copy', '');
          text.append(createWhatsInBoxContent(line));

          item.append(icon, text);
          includedList.prepend(item);
        });
    }

    function updateVariantUi(variant, changedOptionIndex = undefined) {
      const previousVariantId = variantInput.value;
      variantInput.value = String(variant.id);
      const didVariantChange = previousVariantId !== variantInput.value;

      if (didVariantChange) {
        variantInput.dispatchEvent(new Event('input', { bubbles: true }));
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      trackedBasePrice = Number(variant.price || 0);
      trackedBaseComparePrice = Number(variant.compare_at_price || 0);

      const storageAddonPrice = getSelectedStorageAddonPrice();
      const storageAddonComparePrice = getSelectedStorageAddonComparePrice();
      const serviceAddonPrice = getSelectedServiceAddonPrice();
      const displayPrice = Number(variant.price || 0) + storageAddonPrice + serviceAddonPrice;
      const compareAt = Number(variant.compare_at_price || 0);
      const displayCompareAt = compareAt + storageAddonComparePrice;
      const hasDiscount = displayCompareAt > displayPrice;

      priceEls.forEach((priceEl) => {
        priceEl.textContent = formatMoney(formatter, displayPrice);
      });

      if (stickyPriceEl) {
        stickyPriceEl.textContent = formatMoney(formatter, displayPrice);
      }

      compareWrapEls.forEach((compareWrapEl) => {
        compareWrapEl.hidden = !hasDiscount;
      });

      compareEls.forEach((compareEl) => {
        compareEl.textContent = hasDiscount ? formatMoney(formatter, displayCompareAt) : '';
      });

      saveEls.forEach((saveEl) => {
        if (hasDiscount && displayCompareAt > 0) {
          const discountPercent = Math.round(((displayCompareAt - displayPrice) / displayCompareAt) * 100);
          saveEl.textContent = `Save ${discountPercent}%`;
        } else {
          saveEl.textContent = '';
        }
      });

      if (stickyVariantEl) {
        stickyVariantEl.textContent = variant.options.filter(Boolean).join(' · ');
      }

      if (addToCartButton) {
        addToCartButton.disabled = !variant.available;
      }

      if (stickySubmitButton) {
        stickySubmitButton.disabled = !variant.available;
      }

      if (addToCartMain) {
        addToCartMain.textContent = variant.available ? addLabel : soldOutLabel;
      }

      // Only update gallery if color option changed (or on initial load when changedOptionIndex is undefined)
      const shouldUpdateGallery = changedOptionIndex === undefined || changedOptionIndex === colorOptionIndex;
      if (shouldUpdateGallery) {
        const colorValue = (colorOptionIndex >= 0 && Array.isArray(variant.options))
          ? String(variant.options[colorOptionIndex] || '').trim()
          : '';
        gallery.setVisibleMediaIds(getGalleryMediaIdsForVariant(variant), variant.featured_media && variant.featured_media.id, colorValue);
      }

      updateOptionPriceDeltas(Number(variant.price || 0));
      updateCroUi(variant);

      if (didVariantChange) {
        emitVariantChange(variant);
      }
    }

    function getFormProperties(formElement = form) {
      const properties = {};
      const targetForm = formElement instanceof HTMLFormElement ? formElement : form;
      const formData = new FormData(targetForm);

      for (const [key, value] of formData.entries()) {
        if (!key.startsWith('properties[') || !key.endsWith(']')) {
          continue;
        }

        const propertyName = key.slice(11, -1);
        const propertyValue = String(value || '').trim();

        if (!propertyName || !propertyValue) {
          continue;
        }

        properties[propertyName] = propertyValue;
      }

      return properties;
    }

    function setBundledSubmitState(isSubmitting) {
      isBundledSubmitInFlight = isSubmitting;
      const activeVariant =
        variants.find((variant) => String(variant.id) === String(variantInput.value || '')) ||
        initialVariant;
      // For truncated products the active variant may not be in client data.
      // If variantInput has a value, assume it's available (server rendered it).
      const isActiveVariantAvailable = activeVariant
        ? Boolean(activeVariant.available)
        : useServerOptionNavigation && String(variantInput.value || '').trim() !== '';

      if (addToCartButton) {
        addToCartButton.disabled = isSubmitting || !isActiveVariantAvailable;
      }

      if (stickySubmitButton) {
        stickySubmitButton.disabled = isSubmitting || !isActiveVariantAvailable;
      }
    }

    function findOptionValueId(optionIndex, optionValue) {
      if (!Number.isFinite(optionIndex) || String(optionValue || '').trim() === '') {
        return '';
      }

      const matchingButton = Array.from(
        section.querySelectorAll(`[data-option-index="${optionIndex}"][data-option-value-id][data-option-value]`)
      ).find((button) => optionValuesMatch(button.dataset.optionValue, optionValue));

      return matchingButton ? String(matchingButton.dataset.optionValueId || '').trim() : '';
    }

    function getSelectedOptionValueIdsByIndex(preferredOptions = selectedOptions) {
      const selectedIds = [];

      if (Array.isArray(preferredOptions)) {
        preferredOptions.forEach((optionValue, optionIndex) => {
          const optionValueId = findOptionValueId(optionIndex, optionValue);
          if (optionValueId) {
            selectedIds[optionIndex] = optionValueId;
          }
        });
      }

      section
        .querySelectorAll('[data-option-index][data-option-value-id]')
        .forEach((button) => {
          const optionIndex = Number(button.dataset.optionIndex);
          const optionValueId = String(button.dataset.optionValueId || '').trim();
          const isSelected =
            button.classList.contains('is-selected') ||
            button.getAttribute('aria-pressed') === 'true';

          if (
            !Number.isFinite(optionIndex) ||
            selectedIds[optionIndex] ||
            !optionValueId ||
            !isSelected
          ) {
            return;
          }

          // Only consider buttons in visible fabric panels. If the button is
          // inside a fabric panel that is not active, skip it so a stale
          // selection from a hidden panel does not override the correct one.
          const fabricPanel = button.closest('[data-fabric-panel]');
          if (fabricPanel && !fabricPanel.classList.contains('is-active')) {
            return;
          }

          selectedIds[optionIndex] = optionValueId;
        });

      return selectedIds;
    }

    function buildServerOptionValuesUrl(optionIndex, optionValueId, explicitUrl = '') {
      const normalizedValueId = String(optionValueId || '').trim();
      if (!normalizedValueId) {
        return explicitUrl || '';
      }

      const selectedIds = getSelectedOptionValueIdsByIndex();
      selectedIds[optionIndex] = normalizedValueId;

      const orderedIds = selectedIds.filter((valueId) => String(valueId || '').trim() !== '');
      if (orderedIds.length === 0) {
        return explicitUrl || '';
      }

      let nextUrl;
      try {
        nextUrl = explicitUrl
          ? new URL(explicitUrl, window.location.origin)
          : new URL(window.location.href);
      } catch (error) {
        return explicitUrl || '';
      }

      nextUrl.searchParams.delete('variant');
      nextUrl.searchParams.set('option_values', orderedIds.join(','));

      return `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
    }

    async function syncToSelection(optionIndex, optionValue, optionUrl = '') {
      const requestId = ++selectionRequestId;
      const previousOptions = selectedOptions.slice();
      selectedOptions[optionIndex] = optionValue;

      if (shouldRenderSelectionFromServer(optionUrl)) {
        updateOptionButtons();
        updateTabLabels();
        updateFabricTabs();

        // Try client-side variant lookup first. If this succeeds the price
        // and variant ID update immediately without a server round-trip.
        if (hasCompleteVariantData) {
          const variant = findExactVariant();
          if (variant) {
            const previousVariantId = String(variantInput.value || '');
            updateVariantUi(variant, optionIndex);
            if (previousVariantId !== String(variant.id)) {
              updateBrowserUrl(variant.id);
            }
            return;
          }
        }

        // Data is truncated or client-side lookup failed — use server render.
        // Skip fetchFullProductData here because the AJAX API (/products/handle.js)
        // is also capped at 250 variants for these products, so it never helps.
        const serverRendered = await renderSectionForSelection(optionUrl, optionIndex);
        if (serverRendered) {
          return;
        }

        // Server render failed. Attempt closest-available fallback.
        if (!hasCompleteVariantData && productHandle) {
          await fetchFullProductData(productHandle);
          if (requestId !== selectionRequestId) {
            return;
          }
        }
        const fallback = findClosestAvailable(optionIndex, optionValue);
        if (fallback) {
          selectedOptions = Array.isArray(fallback.options)
            ? fallback.options.slice()
            : previousOptions;
          const previousVariantId = String(variantInput.value || '');
          updateVariantUi(fallback, optionIndex);
          if (previousVariantId !== String(fallback.id)) {
            updateBrowserUrl(fallback.id);
          }
        }
        return;
      }

      // Ensure full variant data is loaded BEFORE updating any UI.
      if (productHandle && !hasCompleteVariantData) {
        const loaded = await fetchFullProductData(productHandle);

        if (requestId !== selectionRequestId) {
          return;
        }

        // If we couldn't load full data and server navigation is enabled,
        // fall back to a full section re-render from the server.
        if (!loaded) {
          if (shouldUseServerOptionNavigation() && optionUrl) {
            renderSectionForSelection(optionUrl, optionIndex);
            return;
          }
        }
      }

      // Now update the UI with complete variant data
      updateOptionButtons();
      updateTabLabels();
      updateFabricTabs();

      let variant = findExactVariant();

      if (!variant) {
        // For truncated products, skip findClosestAvailable which picks
        // wrong variants (matching size/headboard but wrong color) and
        // use server rendering instead.
        if (useServerOptionNavigation && optionUrl) {
          renderSectionForSelection(optionUrl, optionIndex);
          return;
        }
        const fallback = findClosestAvailable(optionIndex, optionValue);
        if (fallback) {
          selectedOptions = Array.isArray(fallback.options)
            ? fallback.options.slice()
            : previousOptions;
          variant = fallback;
        }
      }

      if (!variant) {
        selectedOptions = previousOptions;
        updateOptionButtons();
        updateTabLabels();
        updateFabricTabs();

        if (optionUrl) {
          renderSectionForSelection(optionUrl);
          return;
        }
        return;
      }

      const previousVariantId = String(variantInput.value || '');
      updateVariantUi(variant, optionIndex);

      if (previousVariantId !== String(variant.id)) {
        updateBrowserUrl(variant.id);
      }
    }

    section
      .querySelectorAll('[data-option-index][data-option-value]')
      .forEach((button) => {
        button.addEventListener('click', async () => {
          if (button.disabled) {
            return;
          }

          const optionIndex = Number(button.dataset.optionIndex);
          const optionValue = button.dataset.optionValue;
          const optionUrl = button.dataset.optionUrl || '';
          const optionValueId = button.dataset.optionValueId || '';

          if (!Number.isFinite(optionIndex) || !optionValue) {
            return;
          }

          const nextOptionUrl = useServerOptionNavigation
            ? buildServerOptionValuesUrl(optionIndex, optionValueId, optionUrl)
            : optionUrl;

          await trackSelectionSync(syncToSelection(optionIndex, optionValue, nextOptionUrl));
        });
      });

    updateOptionButtons();
    updateTabLabels();
    updateFabricTabs();
    if (!isServerVariantOutsideClientData && !shouldDeferInitialVariantUi) {
      updateVariantUi(initialVariant);
    }

    storageBundle = initStorageBundle(section);
    serviceBundle = initServiceBundle(section);
    initDivanStorageFilter(section);
    initButtonOptionTab(section);

    const mattressBundle = initMattressBundle(section, {
      formatter,
      getCurrentVariant() {
        const activeVariantId = String(variantInput.value || '');
        return (
          variants.find((variant) => String(variant.id) === activeVariantId) ||
          initialVariant
        );
      },
    });

    syncWhatsInBoxSummary();

    form.addEventListener('submit', async (event) => {
      if (isBundledSubmitInFlight) {
        return;
      }

      event.preventDefault();

      const submitTargetForm = getSubmitTargetForm();
      const submitTargetVariantInput = submitTargetForm.querySelector('input[name="id"]');
      const mainVariant = await resolveVariantForSubmission();
      const mainVariantId = Number(mainVariant && mainVariant.id ? mainVariant.id : 0);
      const storageVariantId = Number(storageBundle && storageBundle.hasSelection() ? storageBundle.getSelectedVariantId() || 0 : 0);
      const mattressVariantId = Number(mattressBundle && mattressBundle.hasSelection() ? mattressBundle.getSelectedVariantId() || 0 : 0);
      const selectedServiceItems =
        serviceBundle && typeof serviceBundle.getSelectedItems === 'function'
          ? serviceBundle.getSelectedItems()
          : [];
      const quantity = Math.max(1, Number(form.querySelector('input[name="quantity"]')?.value || 1));
      const hasBundledItems = Boolean(storageVariantId || mattressVariantId || selectedServiceItems.length > 0);

      if (!mainVariantId) {
        return;
      }

      if (submitTargetVariantInput) {
        submitTargetVariantInput.value = String(mainVariantId);
      }

      if (!hasBundledItems) {
        if (typeof HTMLFormElement !== 'undefined' && HTMLFormElement.prototype?.submit) {
          HTMLFormElement.prototype.submit.call(submitTargetForm);
        } else {
          submitTargetForm.submit();
        }
        return;
      }

      setBundledSubmitState(true);

      try {
        const mainProperties = getFormProperties(submitTargetForm);
        const bundleId = `bundle-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        const bundledMainProperties = {
          ...mainProperties,
          _bundle_id: bundleId,
          _bundle_role: 'main',
        };
        const items = [
          {
            id: mainVariantId,
            quantity,
            properties: bundledMainProperties,
          },
        ];

        if (storageVariantId) {
          items.push({
            id: storageVariantId,
            quantity,
            properties: {
              _bundle_id: bundleId,
              _bundle_role: 'storage',
            },
          });
        }

        if (mattressVariantId) {
          items.push({
            id: mattressVariantId,
            quantity,
            properties: {
              _bundle_id: bundleId,
              _bundle_role: 'mattress',
            },
          });
        }

        selectedServiceItems.forEach((serviceItem) => {
          const serviceVariantId = Number(serviceItem.variantId || 0);
          if (!serviceVariantId) {
            return;
          }

          items.push({
            id: serviceVariantId,
            quantity,
            properties: {
              _bundle_id: bundleId,
              _bundle_role: 'service',
            },
          });
        });

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to add bundled items to cart');
        }

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        window.location.href = '/cart';
      } catch (error) {
        console.error('Could not add bundled items to cart:', error);
        const toast = section.querySelector('[data-atc-toast]');
        if (toast) {
          toast.textContent = 'Could not add the selected add-ons to the cart. Please try again.';
          toast.classList.remove('is-success');
          toast.hidden = false;
          setTimeout(() => { toast.hidden = true; }, 6000);
        }
      } finally {
        setBundledSubmitState(false);
      }
    });

    section.addEventListener('storage:change', () => {
      const activeVariantId = String(variantInput.value || '');
      const activeVariant =
        variants.find((variant) => String(variant.id) === activeVariantId);

      if (activeVariant) {
        updateVariantUi(activeVariant);
        syncWhatsInBoxSummary();
      } else {
        updatePriceWithCurrentAddons().then(() => syncWhatsInBoxSummary());
      }
    });

    section.addEventListener('service:change', () => {
      const activeVariantId = String(variantInput.value || '');
      const activeVariant =
        variants.find((variant) => String(variant.id) === activeVariantId);

      if (activeVariant) {
        updateVariantUi(activeVariant);
        syncWhatsInBoxSummary();
      } else {
        updatePriceWithCurrentAddons().then(() => syncWhatsInBoxSummary());
      }
    });

    section.addEventListener('variant:change', syncWhatsInBoxSummary);
    section.addEventListener('button-option:change', syncWhatsInBoxSummary);
    section.addEventListener('mattress:change', syncWhatsInBoxSummary);
  }

  function initStorageBundle(section) {
    const storagePanel = section.querySelector('[data-storage-panel]');
    const productForm = section.querySelector('[data-product-form]');

    if (!storagePanel || !productForm) {
      return null;
    }

    const optionButtons = Array.from(storagePanel.querySelectorAll('[data-storage-option]'));
    const tabValue = section.querySelector('[data-storage-tab-current]');
    const tabButtonId = storagePanel.getAttribute('aria-labelledby') || '';
    const tabButton = tabButtonId ? section.querySelector(`#${tabButtonId}`) : null;
    const tabIcon = tabButton ? tabButton.querySelector('.variant-tab-icon') : null;

    if (optionButtons.length === 0) {
      return null;
    }

    const storageVariantInput = ensureStoragePropertyInput('properties[Storage Add-on Variant ID]');
    const storageTitleInput = ensureStoragePropertyInput('properties[Storage Add-on]');
    let selectedButton =
      optionButtons.find((button) => button.classList.contains('is-selected')) ||
      optionButtons.find((button) => !button.disabled) ||
      optionButtons[0];

    function ensureStoragePropertyInput(name) {
      let input = productForm.querySelector(`input[name="${name}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = '';
        productForm.appendChild(input);
      }

      return input;
    }

    function syncSelection(button) {
      if (!button) {
        if (storageVariantInput) {
          storageVariantInput.value = '';
        }

        if (storageTitleInput) {
          storageTitleInput.value = '';
        }

        if (tabValue) {
          tabValue.textContent = '';
        }

        selectedButton = null;
        return;
      }

      optionButtons.forEach((optionButton) => {
        const isSelected = optionButton === button;
        optionButton.classList.toggle('is-selected', isSelected);
        optionButton.setAttribute('aria-pressed', String(isSelected));
      });

      selectedButton = button;

      const selectedLabel = button.dataset.storageLabel || '';
      const productTitle = button.dataset.storageProductTitle || '';

      if (storageVariantInput) {
        storageVariantInput.value = button.dataset.storageVariantId || '';
      }

      if (storageTitleInput) {
        storageTitleInput.value = [productTitle, selectedLabel].filter(Boolean).join(' - ');
      }

      if (tabValue) {
        tabValue.textContent = selectedLabel;
        tabValue.setAttribute('title', selectedLabel);
      }

      if (tabIcon) {
        const selectedIcon = button.querySelector('.storage-opt-icon');
        if (selectedIcon) {
          tabIcon.innerHTML = selectedIcon.innerHTML;
          tabIcon.classList.toggle('variant-tab-icon--selected-art', Boolean(tabIcon.querySelector('img')));
        }
      }

      const productSection = storagePanel.closest('[data-product-section]');
      if (productSection) {
        productSection.dispatchEvent(
          new CustomEvent('storage:change', {
            bubbles: true,
          })
        );
      }
    }

    optionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) {
          return;
        }

        syncSelection(button);
      });
    });

    syncSelection(selectedButton);

    return {
      hasSelection() {
        return Boolean(selectedButton && !selectedButton.disabled && selectedButton.dataset.storageVariantId);
      },
      getSelectedVariantId() {
        return selectedButton ? selectedButton.dataset.storageVariantId || '' : '';
      },
      getSelectedPrice() {
        return selectedButton ? selectedButton.dataset.storagePrice || '0' : '0';
      },
      getSelectedComparePrice() {
        return selectedButton ? selectedButton.dataset.storageComparePrice || '0' : '0';
      },
    };
  }

  /**
   * Dynamic storage option filtering for products based on:
   * - Product type (wing-headboard tag, divan collection)
   * - Selected bed size (single vs other sizes)
   *
   * Storage option rules:
   * 1. wing-headboard + Divan Collection:
   *    - Same drawer availability as standard divan products
   *
   * 2. NOT wing-headboard + Divan Collection:
   *    - Single (3FT): "No Storage", "2 Drawers Left", "2 Drawers Right"
   *    - Other sizes: "No Storage", "2 Drawers Left", "2 Drawers Right", "2-Drawer Footend", "4 Drawers"
   *
   * 3. NOT in Divan Collection:
   *    - "No Storage (Slatted Base)", "No Storage (Blackboard)", "Metal gas lift storage + Blackboard"
   */
  function initDivanStorageFilter(section) {
    const storageBundlePanel = section.querySelector('[data-wing-headboard-storage-bundle]');
    const storageOptionsPanel = section.querySelector('[data-wing-headboard-storage]');

    if (!storageBundlePanel && !storageOptionsPanel) {
      return null;
    }

    // Check if this is a wing-headboard product
    const isWingHeadboard = storageOptionsPanel?.dataset.isWingHeadboard === 'true';

    // Check if this is a divan collection product
    const isDivanProduct = storageBundlePanel?.hasAttribute('data-divan-collection') ||
                            storageOptionsPanel?.hasAttribute('data-divan-collection');

    // Allowed options for wing-headboard + divan collection based on bed size
    const wingHeadboardDivanSingleOptions = ['no storage', '2 drawers left', '2 drawers right'];
    const wingHeadboardDivanOtherOptions = ['no storage', '2 drawers left', '2 drawers right', '2-drawer footend', '4 drawers'];

    // Allowed options for divan collection (NOT wing-headboard) based on bed size
    const divanSingleOptions = ['no storage', '2 drawers left', '2 drawers right'];
    const divanOtherOptions = ['no storage', '2 drawers left', '2 drawers right', '2-drawer footend', '4 drawers'];

    // Allowed options for NON-divan products - only these will be shown on other collections
    const nonDivanAllowedOptions = [
      'no storage (slatted base)',
      'no storage (blackboard)',
      'metal gas lift storage + blackboard'
    ];

    function isSingleBed(sizeLabel) {
      if (!sizeLabel) return false;
      const sizeDown = sizeLabel.toLowerCase().trim();
      return sizeDown.includes('single') || sizeDown.includes('3ft');
    }

    function getAllowedOptions(isSingle) {
      // Priority: wing-headboard + divan > divan only > non-divan
      if (isWingHeadboard && isDivanProduct) {
        // wing-headboard + Divan Collection: same options as standard divan products
        return isSingle ? wingHeadboardDivanSingleOptions : wingHeadboardDivanOtherOptions;
      } else if (isDivanProduct) {
        // NOT wing-headboard + Divan Collection: Full divan options
        return isSingle ? divanSingleOptions : divanOtherOptions;
      } else {
        // NOT in Divan Collection: Non-divan options only
        return nonDivanAllowedOptions;
      }
    }

    function filterStorageOptions(isSingle) {
      const allowedOptions = getAllowedOptions(isSingle);

      // Filter storage bundle options (from metafield product)
      if (storageBundlePanel) {
        const storageButtons = storageBundlePanel.querySelectorAll('[data-storage-option]');
        storageButtons.forEach((button) => {
          const label = (button.dataset.storageLabel || '').toLowerCase().trim();
          const isVisible = allowedOptions.includes(label);

          button.style.display = isVisible ? '' : 'none';

          // If the currently selected option is now hidden, select the first visible one
          if (!isVisible && button.classList.contains('is-selected')) {
            button.classList.remove('is-selected');
            button.setAttribute('aria-pressed', 'false');

            // Find and select the first visible option
            const firstVisibleButton = Array.from(storageButtons).find(
              (btn) => {
                const btnLabel = (btn.dataset.storageLabel || '').toLowerCase().trim();
                return allowedOptions.includes(btnLabel) && !btn.disabled;
              }
            );
            if (firstVisibleButton) {
              firstVisibleButton.click();
            }
          }
        });
      }

      // Filter storage options within product variants
      if (storageOptionsPanel) {
        const storageButtons = storageOptionsPanel.querySelectorAll('[data-option-value]');
        storageButtons.forEach((button) => {
          const label = (button.dataset.optionValue || '').toLowerCase().trim();
          const isVisible = allowedOptions.includes(label);

          button.style.display = isVisible ? '' : 'none';

          // If the currently selected option is now hidden, select the first visible one
          if (!isVisible && button.classList.contains('is-selected')) {
            button.classList.remove('is-selected');
            button.setAttribute('aria-pressed', 'false');

            // Find and select the first visible option
            const firstVisibleButton = Array.from(storageButtons).find(
              (btn) => {
                const btnLabel = (btn.dataset.optionValue || '').toLowerCase().trim();
                return allowedOptions.includes(btnLabel) && !btn.disabled;
              }
            );
            if (firstVisibleButton) {
              firstVisibleButton.click();
            }
          }
        });
      }
    }

    // Listen for variant changes
    section.addEventListener('variant:change', (event) => {
      const variant = event.detail?.variant;
      if (!variant) return;

      // Find the size option index by looking at the tab labels
      let sizeOptionIndex = -1;
      const tabLabels = section.querySelectorAll('.variant-tab-label');
      tabLabels.forEach((label, index) => {
        if (label.textContent.toLowerCase().includes('size')) {
          sizeOptionIndex = index;
        }
      });

      if (sizeOptionIndex !== -1 && variant.options && variant.options[sizeOptionIndex]) {
        const selectedSize = variant.options[sizeOptionIndex];
        filterStorageOptions(isSingleBed(selectedSize));
      }
    });

    // Initial filter based on current selection - find the size tab
    const sizeTab = section.querySelector('.variant-tab');
    if (sizeTab) {
      // Find the size option by checking all tabs
      const allTabs = section.querySelectorAll('.variant-tab');
      let sizeValue = '';

      allTabs.forEach((tab, index) => {
        const tabLabel = tab.querySelector('.variant-tab-label');
        if (tabLabel && tabLabel.textContent.toLowerCase().includes('size')) {
          const tabValue = tab.querySelector('[data-tab-current]');
          if (tabValue) {
            sizeValue = tabValue.textContent.trim();
          }
        }
      });

      if (sizeValue) {
        filterStorageOptions(isSingleBed(sizeValue));
      }
    }

    return {
      refreshFilter(sizeLabel) {
        filterStorageOptions(isSingleBed(sizeLabel));
      },
    };
  }

  function initServiceBundle(section) {
    const servicePanel = section.querySelector('[data-service-bundle]');
    const productForm = section.querySelector('[data-product-form]');
    const serviceTab = section.querySelector('[data-service-toggle]');
    const serviceOverlay = section.querySelector('[data-service-overlay]');
    const serviceDrawer = section.querySelector('[data-service-drawer]');
    const closeButtons = Array.from(section.querySelectorAll('[data-service-close]'));
    const tabEyebrow = section.querySelector('[data-service-tab-eyebrow]');
    const tabHeadline = section.querySelector('[data-service-tab-headline]');
    const tabSubtitle = section.querySelector('[data-service-tab-sub]');
    const tabChange = section.querySelector('[data-service-tab-change]');

    if (!servicePanel || !productForm) {
      return null;
    }

    const optionButtons = Array.from(servicePanel.querySelectorAll('[data-service-option]'));
    if (optionButtons.length === 0) {
      return null;
    }

    const formatter = moneyFormatter(
      document.documentElement.lang || 'en-GB',
      section.dataset.currency || 'GBP'
    );
    const hasDrawerUi = Boolean(serviceTab && serviceOverlay && serviceDrawer);
    const defaultTabState = {
      eyebrow: tabEyebrow ? tabEyebrow.textContent : '',
      headline: tabHeadline ? tabHeadline.textContent : '',
      subtitle: tabSubtitle ? tabSubtitle.textContent : '',
    };
    const selectedButtons = new Set();
    const serviceIdsInput = ensureServicePropertyInput('properties[_Service Add-on Variant IDs]');
    const serviceTitlesInput = ensureServicePropertyInput('properties[Service Add-ons]');

    function ensureServicePropertyInput(name) {
      let input = productForm.querySelector(`input[name="${name}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = '';
        productForm.appendChild(input);
      }

      return input;
    }

    function getSelectedButtons() {
      return optionButtons.filter((button) => selectedButtons.has(button));
    }

    function getSelectedTotalPrice() {
      return getSelectedButtons().reduce((total, button) => {
        return total + Number(button.dataset.servicePrice || 0);
      }, 0);
    }

    function updatePropertyInputs() {
      const selected = getSelectedButtons();

      if (serviceIdsInput) {
        serviceIdsInput.value = selected
          .map((button) => button.dataset.serviceVariantId || '')
          .filter(Boolean)
          .join(',');
      }

      if (serviceTitlesInput) {
        serviceTitlesInput.value = selected
          .map((button) => {
            const groupLabel = button.dataset.serviceGroupLabel || '';
            const title = button.dataset.serviceTitle || '';
            const price = Number(button.dataset.servicePrice || 0);
            const priceLabel = `(+${formatMoney(formatter, price)})`;
            return [groupLabel, title, priceLabel].filter(Boolean).join(' - ');
          })
          .filter(Boolean)
          .join(', ');
      }
    }

    function updateTabState() {
      if (!serviceTab) {
        return;
      }

      const selected = getSelectedButtons();
      const hasSelection = selected.length > 0;
      const totalPrice = getSelectedTotalPrice();

      serviceTab.classList.toggle('is-added', hasSelection);
      serviceTab.setAttribute(
        'aria-label',
        hasSelection ? 'Edit selected optional services' : 'Open optional services'
      );

      if (tabEyebrow) {
        tabEyebrow.textContent = hasSelection ? 'Optional services added' : defaultTabState.eyebrow;
      }

      if (tabHeadline) {
        tabHeadline.textContent = hasSelection
          ? selected.length === 1
            ? selected[0].dataset.serviceTitle || '1 service selected'
            : `${selected.length} services selected`
          : defaultTabState.headline;
      }

      if (tabSubtitle) {
        tabSubtitle.textContent = hasSelection
          ? `Added ${formatMoney(formatter, totalPrice)}${selected.length > 1 ? ' total' : ''}`
          : defaultTabState.subtitle;
      }

      if (tabChange) {
        tabChange.classList.toggle('is-visible', hasSelection);
      }
    }

    function syncButtonState(button) {
      const isSelected = selectedButtons.has(button);
      const card = button.closest('[data-service-option-card]');
      const labelNode = button.querySelector('[data-service-button-label]');

      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', String(isSelected));

      if (card) {
        card.classList.toggle('is-selected', isSelected);
      }

      if (labelNode) {
        labelNode.textContent = isSelected ? 'Remove' : 'Add';
      }
    }

    function emitChange() {
      const productSection = servicePanel.closest('[data-product-section]');
      if (!productSection) {
        return;
      }

      productSection.dispatchEvent(
        new CustomEvent('service:change', {
          bubbles: true,
        })
      );
    }

    function getFocusableElements() {
      if (!serviceDrawer) {
        return [];
      }

      return Array.from(
        serviceDrawer.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.disabled && element.offsetParent !== null);
    }

    function openDrawer() {
      if (!hasDrawerUi) {
        return;
      }

      serviceOverlay.classList.add('open');
      serviceOverlay.setAttribute('aria-hidden', 'false');
      serviceDrawer.classList.add('open');
      serviceDrawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      const firstCloseButton = serviceDrawer.querySelector('[data-service-close]');
      if (firstCloseButton) {
        setTimeout(() => firstCloseButton.focus(), 100);
      }
    }

    function closeDrawer() {
      if (!hasDrawerUi) {
        return;
      }

      serviceOverlay.classList.remove('open');
      serviceOverlay.setAttribute('aria-hidden', 'true');
      serviceDrawer.classList.remove('open');
      serviceDrawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      serviceTab.focus();
    }

    optionButtons.forEach((button) => {
      syncButtonState(button);

      button.addEventListener('click', () => {
        if (selectedButtons.has(button)) {
          selectedButtons.delete(button);
        } else {
          selectedButtons.add(button);
        }

        syncButtonState(button);
        updatePropertyInputs();
        updateTabState();
        emitChange();
      });
    });

    updatePropertyInputs();
    updateTabState();

    if (hasDrawerUi) {
      serviceTab.addEventListener('click', (event) => {
        const arrowContainer = serviceTab.querySelector('.mattress-tab-arrow');
        const isCloseClick = arrowContainer && (event.target === arrowContainer || arrowContainer.contains(event.target));
        if (isCloseClick && serviceTab.classList.contains('is-added')) {
          event.stopPropagation();
          selectedButtons.clear();
          optionButtons.forEach((btn) => syncButtonState(btn));
          updatePropertyInputs();
          updateTabState();
          emitChange();
          return;
        }
        openDrawer();
      });
      serviceOverlay.addEventListener('click', closeDrawer);
      closeButtons.forEach((button) => {
        button.addEventListener('click', closeDrawer);
      });

      serviceDrawer.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeDrawer();
          return;
        }

        if (event.key !== 'Tab') {
          return;
        }

        const focusables = getFocusableElements();
        if (focusables.length === 0) {
          return;
        }

        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }

    return {
      hasSelection() {
        return selectedButtons.size > 0;
      },
      getSelectedItems() {
        return getSelectedButtons()
          .map((button) => {
            const variantId = Number(button.dataset.serviceVariantId || 0);
            if (!variantId) {
              return null;
            }

            return {
              variantId,
              title: button.dataset.serviceTitle || '',
              price: Number(button.dataset.servicePrice || 0),
            };
          })
          .filter(Boolean);
      },
      getSelectedPrice() {
        return getSelectedTotalPrice();
      },
    };
  }

  function initButtonOptionTab(section) {
    const buttonOptionPanel = section.querySelector('[data-button-option-panel]');
    const productForm = section.querySelector('[data-product-form]');

    if (!buttonOptionPanel || !productForm) {
      return null;
    }

    const optionButtons = Array.from(buttonOptionPanel.querySelectorAll('[data-button-option-choice]'));
    const tabValue = section.querySelector('[data-button-option-tab-current]');

    if (optionButtons.length === 0) {
      return null;
    }

    let propertyInput = productForm.querySelector('input[name="properties[Button Option]"]');
    if (!propertyInput) {
      propertyInput = document.createElement('input');
      propertyInput.type = 'hidden';
      propertyInput.name = 'properties[Button Option]';
      propertyInput.value = '';
      productForm.appendChild(propertyInput);
    }

    let selectedButton =
      optionButtons.find((button) => button.classList.contains('is-selected')) ||
      optionButtons[0];

    function syncSelection(button) {
      selectedButton = button || null;

      optionButtons.forEach((optionButton) => {
        const isSelected = optionButton === selectedButton;
        optionButton.classList.toggle('is-selected', isSelected);
        optionButton.setAttribute('aria-pressed', String(isSelected));
      });

      const selectedLabel = selectedButton ? selectedButton.dataset.buttonOptionLabel || '' : '';

      if (propertyInput) {
        propertyInput.value = selectedLabel;
      }

      if (tabValue) {
        tabValue.textContent = selectedLabel;
        tabValue.setAttribute('title', selectedLabel);
      }

      const productSection = buttonOptionPanel.closest('[data-product-section]');
      if (productSection) {
        productSection.dispatchEvent(
          new CustomEvent('button-option:change', {
            bubbles: true,
          })
        );
      }
    }

    optionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        syncSelection(button);
      });
    });

    syncSelection(selectedButton);

    return {
      getSelectedValue() {
        return propertyInput ? propertyInput.value : '';
      },
    };
  }

  function initMattressBundle(section, options = {}) {
    const mattressTab = section.querySelector('[data-mattress-toggle]');
    const mattressOverlay = section.querySelector('[data-mattress-overlay]');
    const mattressDrawer = section.querySelector('[data-mattress-drawer]');
    const closeButtons = section.querySelectorAll('[data-mattress-close]');
    const drawerEyebrow = section.querySelector('[data-mattress-drawer-eyebrow]');
    const drawerTitle = section.querySelector('[data-mattress-drawer-title]');
    const progressFill = section.querySelector('[data-mattress-progress]');
    const dotNodes = Array.from(section.querySelectorAll('[data-mattress-dot]'));
    const steps = Array.from(section.querySelectorAll('[data-quiz-step]'));
    const recoTitleNode = section.querySelector('[data-mattress-reco-title]');
    const sizeLabelNode = section.querySelector('[data-mattress-size-label]');
    const mattressList = section.querySelector('[data-mattress-results-list]');
    const productForm = section.querySelector('[data-product-form]');

    if (!mattressTab || !mattressOverlay || !mattressDrawer || steps.length === 0 || !mattressList) {
      return {
        hasSelection() {
          return false;
        },
        getSelectedVariantId() {
          return '';
        },
      };
    }

    const formatter =
      options.formatter ||
      moneyFormatter(document.documentElement.lang || 'en-GB', section.dataset.currency || 'GBP');
    const getCurrentVariant =
      typeof options.getCurrentVariant === 'function' ? options.getCurrentVariant : () => null;

    const MATTRESS_PRODUCTS = [
  {
    "productId": 9409793720649,
    "handle": "dorchester-orthopedic-mattress",
    "title": "Dorchester orthopedic Mattress",
    "description": "The Dorchester Orthopedic Mattress is designed to provide a balance between support and comfort.",
    "image": "https://cdn.shopify.com/s/files/1/0886/6155/7577/files/dorchester-orthopedic-mattress-single-3ft-563595.webp?v=1748473247",
    "variants": [
      {
        "id": 51185682743625,
        "title": "Single (3FT)",
        "price": 16999,
        "compareAtPrice": 42500,
        "available": true
      },
      {
        "id": 51185682776393,
        "title": "Small Double (4FT)",
        "price": 18999,
        "compareAtPrice": 47500,
        "available": true
      },
      {
        "id": 51185682809161,
        "title": "Double (4FT6)",
        "price": 19999,
        "compareAtPrice": 50000,
        "available": true
      },
      {
        "id": 51185682841929,
        "title": "King (5FT)",
        "price": 20999,
        "compareAtPrice": 52500,
        "available": true
      },
      {
        "id": 51185682874697,
        "title": "Super King (6FT)",
        "price": 21999,
        "compareAtPrice": 55000,
        "available": true
      }
    ]
  },
  {
    "productId": 9409850900809,
    "handle": "memory-deluxe-mattress",
    "title": "Memory Deluxe Mattress",
    "description": "The Memory Deluxe mattress is a type of hybrid mattress that combines memory foam technology with traditional Bonnell spring support.",
    "image": "https://cdn.shopify.com/s/files/1/0886/6155/7577/files/memory-deluxe-mattress-single-3ft-440570.webp?v=1748473253",
    "variants": [
      {
        "id": 51185983816009,
        "title": "Single (3FT)",
        "price": 22999,
        "compareAtPrice": 57500,
        "available": true
      },
      {
        "id": 51185983848777,
        "title": "Small Double (4FT)",
        "price": 24499,
        "compareAtPrice": 61250,
        "available": true
      },
      {
        "id": 51185983881545,
        "title": "Double (4FT6)",
        "price": 24999,
        "compareAtPrice": 62500,
        "available": true
      },
      {
        "id": 51185983914313,
        "title": "King (5FT)",
        "price": 26499,
        "compareAtPrice": 66250,
        "available": true
      },
      {
        "id": 51185983947081,
        "title": "Super King (6FT)",
        "price": 28999,
        "compareAtPrice": 72500,
        "available": true
      }
    ]
  },
  {
    "productId": 9409837662537,
    "handle": "oxford-pillowtop-matress",
    "title": "Oxford Pillowtop Matress",
    "description": "The Oxford pillowtop mattress has a 12.5g Bonnell spring system which combines comfort and support.",
    "image": "https://cdn.shopify.com/s/files/1/0886/6155/7577/files/oxford-pillowtop-matress-single-3ft-899379.webp?v=1748473250",
    "variants": [
      {
        "id": 51185917755721,
        "title": "Single (3FT)",
        "price": 25999,
        "compareAtPrice": 65000,
        "available": true
      },
      {
        "id": 51185917788489,
        "title": "Small Double (4FT)",
        "price": 27999,
        "compareAtPrice": 70000,
        "available": true
      },
      {
        "id": 51185917821257,
        "title": "Double (4FT6)",
        "price": 28499,
        "compareAtPrice": 71250,
        "available": true
      },
      {
        "id": 51185917854025,
        "title": "King (5FT)",
        "price": 30999,
        "compareAtPrice": 77500,
        "available": true
      },
      {
        "id": 51185917886793,
        "title": "Super King (6FT)",
        "price": 33999,
        "compareAtPrice": 85000,
        "available": true
      }
    ]
  },
  {
    "productId": 9409814495561,
    "handle": "backcare-1000-mattress",
    "title": "Backcare 1000 Mattress",
    "description": "The Backcare 1000 Pocket Springs Firm Mattress is designed to provide excellent support and comfort with a focus on back health.",
    "image": "https://cdn.shopify.com/s/files/1/0886/6155/7577/files/backcare-1000-mattress-single-3ft-594874.webp?v=1748473253",
    "variants": [
      {
        "id": 51185795006793,
        "title": "Single (3FT)",
        "price": 24999,
        "compareAtPrice": 62500,
        "available": true
      },
      {
        "id": 51185795039561,
        "title": "Small Double (4FT)",
        "price": 27999,
        "compareAtPrice": 70000,
        "available": true
      },
      {
        "id": 51185795072329,
        "title": "Double (4FT6)",
        "price": 28499,
        "compareAtPrice": 71250,
        "available": true
      },
      {
        "id": 51185795105097,
        "title": "King (5FT)",
        "price": 30999,
        "compareAtPrice": 77500,
        "available": true
      },
      {
        "id": 51185795137865,
        "title": "Super King (6FT)",
        "price": 33999,
        "compareAtPrice": 85000,
        "available": true
      }
    ]
  },
  {
    "productId": 9409845821769,
    "handle": "sapphire-pillowtop-2000-mattress",
    "title": "Sapphire Pillowtop 2000 Mattress",
    "description": "The Sapphire Pillowtop 2000 Pocket Luxury Mattress is designed to offer a high level of comfort, support, and durability, making it ideal for individuals seeking a premium sleep ex...",
    "image": "https://cdn.shopify.com/s/files/1/0886/6155/7577/files/sapphire-pillowtop-2000-mattress-single-3ft-754597.webp?v=1748473251",
    "variants": [
      {
        "id": 51185956847945,
        "title": "Single (3FT)",
        "price": 31999,
        "compareAtPrice": 80000,
        "available": true
      },
      {
        "id": 51185956880713,
        "title": "Small Double (4FT)",
        "price": 33999,
        "compareAtPrice": 85000,
        "available": true
      },
      {
        "id": 51185956913481,
        "title": "Double (4FT6)",
        "price": 34499,
        "compareAtPrice": 86250,
        "available": true
      },
      {
        "id": 51185956946249,
        "title": "King (5FT)",
        "price": 36999,
        "compareAtPrice": 92500,
        "available": true
      },
      {
        "id": 51185956979017,
        "title": "Super King (6FT)",
        "price": 39499,
        "compareAtPrice": 98750,
        "available": true
      }
    ]
  },
  {
    "productId": 9409835237705,
    "handle": "super-reflex-foam-mattress",
    "title": "Super Reflex Foam Mattress",
    "description": "A Super Reflex Foam Mattress typically features high-density foam designed to offer enhanced support and comfort.",
    "image": "https://cdn.shopify.com/s/files/1/0886/6155/7577/files/super-reflex-foam-mattress-single-3ft-292071.webp?v=1748473243",
    "variants": [
      {
        "id": 51185901797705,
        "title": "Single (3FT)",
        "price": 23999,
        "compareAtPrice": 60000,
        "available": true
      },
      {
        "id": 51185901830473,
        "title": "Small Double (4FT)",
        "price": 24999,
        "compareAtPrice": 62500,
        "available": true
      },
      {
        "id": 51185901863241,
        "title": "Double (4FT6)",
        "price": 25499,
        "compareAtPrice": 63750,
        "available": true
      },
      {
        "id": 51185901896009,
        "title": "King (5FT)",
        "price": 27999,
        "compareAtPrice": 70000,
        "available": true
      },
      {
        "id": 51185901928777,
        "title": "Super King (6FT)",
        "price": 30499,
        "compareAtPrice": 76250,
        "available": true
      }
    ]
  },
  {
    "productId": 9409833304393,
    "handle": "dorset-pocket-1000-matress",
    "title": "Dorset Pocket 1000 Matress",
    "description": "The Dorset Pocket 1000 Mattress is a medium-firm option designed for optimal support and comfort.",
    "image": "https://cdn.shopify.com/s/files/1/0886/6155/7577/files/dorset-pocket-1000-matress-single-3ft-827647.webp?v=1748473250",
    "variants": [
      {
        "id": 51185890656585,
        "title": "Single (3FT)",
        "price": 24999,
        "compareAtPrice": 62500,
        "available": true
      },
      {
        "id": 51185890689353,
        "title": "Small Double (4FT)",
        "price": 26999,
        "compareAtPrice": 67500,
        "available": true
      },
      {
        "id": 51185890722121,
        "title": "Double (4FT6)",
        "price": 27499,
        "compareAtPrice": 68750,
        "available": true
      },
      {
        "id": 51185890754889,
        "title": "King (5FT)",
        "price": 28999,
        "compareAtPrice": 72500,
        "available": true
      },
      {
        "id": 51185890787657,
        "title": "Super King (6FT)",
        "price": 31999,
        "compareAtPrice": 80000,
        "available": true
      }
    ]
  },
  {
    "productId": 9409830420809,
    "handle": "swansea-2000-pocket-matress",
    "title": "Swansea 2000 Pocket Matress",
    "description": "The Swansea 2000 Pocket Mattress in Soft Medium is designed to offer a balance of comfort and support, ideal for those who enjoy a softer sleeping surface but still need sufficient...",
    "image": "https://cdn.shopify.com/s/files/1/0886/6155/7577/files/swansea-2000-pocket-matress-single-3ft-542514.webp?v=1748473250",
    "variants": [
      {
        "id": 51185873715529,
        "title": "Single (3FT)",
        "price": 28499,
        "compareAtPrice": 71250,
        "available": true
      },
      {
        "id": 51185873748297,
        "title": "Small Double (4FT)",
        "price": 30499,
        "compareAtPrice": 76250,
        "available": true
      },
      {
        "id": 51185873781065,
        "title": "Double (4FT6)",
        "price": 30999,
        "compareAtPrice": 77500,
        "available": true
      },
      {
        "id": 51185873813833,
        "title": "King (5FT)",
        "price": 33999,
        "compareAtPrice": 85000,
        "available": true
      },
      {
        "id": 51185873846601,
        "title": "Super King (6FT)",
        "price": 36999,
        "compareAtPrice": 92500,
        "available": true
      }
    ]
  },
  {
    "productId": 9409827275081,
    "handle": "tencel-1500-pocket-mattress",
    "title": "Tencel 1500 Pocket Mattress",
    "description": "The Tencel 1500 Pocket Mattress is a luxurious and high-quality mattress designed to provide exceptional comfort and support for a restful night's sleep.",
    "image": "https://cdn.shopify.com/s/files/1/0886/6155/7577/files/tencel-1500-pocket-mattress-single-3ft-228971.webp?v=1748473252",
    "variants": [
      {
        "id": 51185859658057,
        "title": "Single (3FT)",
        "price": 26999,
        "compareAtPrice": 67500,
        "available": true
      },
      {
        "id": 51185859690825,
        "title": "Small Double (4FT)",
        "price": 28999,
        "compareAtPrice": 72500,
        "available": true
      },
      {
        "id": 51185859723593,
        "title": "Double (4FT6)",
        "price": 30999,
        "compareAtPrice": 77500,
        "available": true
      },
      {
        "id": 51185859756361,
        "title": "King (5FT)",
        "price": 32499,
        "compareAtPrice": 81250,
        "available": true
      },
      {
        "id": 51185859789129,
        "title": "Super King (6FT)",
        "price": 34999,
        "compareAtPrice": 87500,
        "available": true
      }
    ]
  },
  {
    "productId": 9409826160969,
    "handle": "chester-wool-2000-pocket-mattress",
    "title": "Chester Wool 2000 Pocket Mattress",
    "description": "The Chester Wool 2000 Pocket Mattress is designed to offer a luxurious sleep experience with a soft, plush feel.",
    "image": "https://cdn.shopify.com/s/files/1/0886/6155/7577/files/chester-wool-2000-pocket-mattress-single-3ft-691268.webp?v=1748473247",
    "variants": [
      {
        "id": 51185852383561,
        "title": "Single (3FT)",
        "price": 31999,
        "compareAtPrice": 80000,
        "available": true
      },
      {
        "id": 51185852416329,
        "title": "Small Double (4FT)",
        "price": 33999,
        "compareAtPrice": 85000,
        "available": true
      },
      {
        "id": 51185852449097,
        "title": "Double (4FT6)",
        "price": 34499,
        "compareAtPrice": 86250,
        "available": true
      },
      {
        "id": 51185852481865,
        "title": "King (5FT)",
        "price": 36999,
        "compareAtPrice": 92500,
        "available": true
      },
      {
        "id": 51185852514633,
        "title": "Super King (6FT)",
        "price": 39999,
        "compareAtPrice": 100000,
        "available": true
      }
    ]
  },
  {
    "productId": 9409823408457,
    "handle": "mroydon-silk-1500-pocket-mattress",
    "title": "Croydon Silk 1500 Pocket Mattress",
    "description": "The Croydon Silk 1500 Pocket Mattress is designed for comfort and support, featuring 1500 individually wrapped pocket springs that adapt to the contours of your body.",
    "image": "https://cdn.shopify.com/s/files/1/0886/6155/7577/files/croydon-silk-1500-pocket-mattress-single-3ft-916582.webp?v=1748473245",
    "variants": [
      {
        "id": 51185833738569,
        "title": "Single (3FT)",
        "price": 26999,
        "compareAtPrice": 67500,
        "available": true
      },
      {
        "id": 51185833771337,
        "title": "Small Double (4FT)",
        "price": 28999,
        "compareAtPrice": 72500,
        "available": true
      },
      {
        "id": 51185833804105,
        "title": "Double (4FT6)",
        "price": 29499,
        "compareAtPrice": 73750,
        "available": true
      },
      {
        "id": 51185833836873,
        "title": "King (5FT)",
        "price": 32499,
        "compareAtPrice": 81250,
        "available": true
      },
      {
        "id": 51185833869641,
        "title": "Super King (6FT)",
        "price": 34999,
        "compareAtPrice": 87500,
        "available": true
      }
    ]
  },
  {
    "productId": 9409821770057,
    "handle": "silk-memory-1000-mattress",
    "title": "Silk memory 1000 Mattress",
    "description": "The 1000 Pocket Silk Memory Mattress is designed to offer a medium-soft feel, providing an ideal balance between support and comfort.",
    "image": "https://cdn.shopify.com/s/files/1/0886/6155/7577/files/silk-memory-1000-mattress-single-3ft-657180.webp?v=1748473246",
    "variants": [
      {
        "id": 51185825022281,
        "title": "Single (3FT)",
        "price": 24999,
        "compareAtPrice": 62500,
        "available": true
      },
      {
        "id": 51185825055049,
        "title": "Small Double (4FT)",
        "price": 26999,
        "compareAtPrice": 67500,
        "available": true
      },
      {
        "id": 51185825087817,
        "title": "Double (4FT6)",
        "price": 28499,
        "compareAtPrice": 71250,
        "available": true
      },
      {
        "id": 51185825120585,
        "title": "King (5FT)",
        "price": 30499,
        "compareAtPrice": 76250,
        "available": true
      },
      {
        "id": 51185825153353,
        "title": "Super King (6FT)",
        "price": 33999,
        "compareAtPrice": 85000,
        "available": true
      }
    ]
  },
  {
    "productId": 9409819509065,
    "handle": "backcare-2000-mattress",
    "title": "Backcare 2000 Mattress",
    "description": "The Backcare 2000 Pocket Springs Firm Mattress is designed to provide excellent support and comfort with a focus on back health.",
    "image": "https://cdn.shopify.com/s/files/1/0886/6155/7577/files/backcare-2000-mattress-single-3ft-488300.webp?v=1748473252",
    "variants": [
      {
        "id": 51185814733129,
        "title": "Single (3FT)",
        "price": 27999,
        "compareAtPrice": 70000,
        "available": true
      },
      {
        "id": 51185814765897,
        "title": "Small Double (4FT)",
        "price": 30499,
        "compareAtPrice": 76250,
        "available": true
      },
      {
        "id": 51185814798665,
        "title": "Double (4FT6)",
        "price": 31999,
        "compareAtPrice": 80000,
        "available": true
      },
      {
        "id": 51185814831433,
        "title": "King (5FT)",
        "price": 34499,
        "compareAtPrice": 86250,
        "available": true
      },
      {
        "id": 51185814864201,
        "title": "Super King (6FT)",
        "price": 38999,
        "compareAtPrice": 97500,
        "available": true
      }
    ]
  },
  {
    "productId": 9409838973257,
    "handle": "richmond-pillowtop-3000-pocket",
    "title": "Richmond Pillowtop 3000 Pocket",
    "description": "The Richmond Pillowtop 3000 Pocket Mattress is designed to provide luxurious comfort and support, ideal for those seeking a restful sleep experience.",
    "image": "https://cdn.shopify.com/s/files/1/0886/6155/7577/files/richmond-pillowtop-3000-pocket-single-3ft-873756.webp?v=1748473244",
    "variants": [
      {
        "id": 51185924571465,
        "title": "Single (3FT)",
        "price": 38999,
        "compareAtPrice": 97500,
        "available": true
      },
      {
        "id": 51185924604233,
        "title": "Small Double (4FT)",
        "price": 41999,
        "compareAtPrice": 105000,
        "available": true
      },
      {
        "id": 51185924637001,
        "title": "Double (4FT6)",
        "price": 42499,
        "compareAtPrice": 106250,
        "available": true
      },
      {
        "id": 51185924669769,
        "title": "King (5FT)",
        "price": 44499,
        "compareAtPrice": 111250,
        "available": true
      },
      {
        "id": 51185924702537,
        "title": "Super King (6FT)",
        "price": 48999,
        "compareAtPrice": 122500,
        "available": true
      }
    ]
  },
  {
    "productId": 9409808925001,
    "handle": "oxford-mattress",
    "title": "Oxford Mattress",
    "description": "The Oxford 12.5g Bonnell Spring Mattress is a well-constructed and affordable mattress designed to provide reliable comfort and support.",
    "image": "https://cdn.shopify.com/s/files/1/0886/6155/7577/files/oxford-mattress-single-3ft-245384.webp?v=1748473245",
    "variants": [
      {
        "id": 51185767711049,
        "title": "Single (3FT)",
        "price": 18499,
        "compareAtPrice": 46250,
        "available": true
      },
      {
        "id": 51185767743817,
        "title": "Small Double (4FT)",
        "price": 19499,
        "compareAtPrice": 48750,
        "available": true
      },
      {
        "id": 51185767776585,
        "title": "Double (4FT6)",
        "price": 20499,
        "compareAtPrice": 51250,
        "available": true
      },
      {
        "id": 51185767809353,
        "title": "King (5FT)",
        "price": 22499,
        "compareAtPrice": 56250,
        "available": true
      },
      {
        "id": 51185767842121,
        "title": "Super King (6FT)",
        "price": 23999,
        "compareAtPrice": 60000,
        "available": true
      }
    ]
  },
  {
    "productId": 9409832386889,
    "handle": "supreme-2500-pocket-matress",
    "title": "Supreme 2500 Pocket Matress",
    "description": "The S upreme 2500 Pocket Mattress in a medium-soft comfort level typically combines advanced pocket spring technology with high-quality materials to provide a balance of comfort an...",
    "image": "https://cdn.shopify.com/s/files/1/0886/6155/7577/files/supreme-2500-pocket-matress-single-3ft-543074.webp?v=1748473254",
    "variants": [
      {
        "id": 51185885577545,
        "title": "Single (3FT)",
        "price": 29999,
        "compareAtPrice": 75000,
        "available": true
      },
      {
        "id": 51185885610313,
        "title": "Small Double (4FT)",
        "price": 31499,
        "compareAtPrice": 78750,
        "available": true
      },
      {
        "id": 51185885643081,
        "title": "Double (4FT6)",
        "price": 31999,
        "compareAtPrice": 80000,
        "available": true
      },
      {
        "id": 51185885675849,
        "title": "King (5FT)",
        "price": 34999,
        "compareAtPrice": 87500,
        "available": true
      },
      {
        "id": 51185885708617,
        "title": "Super King (6FT)",
        "price": 38499,
        "compareAtPrice": 96250,
        "available": true
      }
    ]
  },
  {
    "productId": 9556627849545,
    "handle": "luxury-waterproof-mattress",
    "title": "Luxury waterproof mattress",
    "description": "The Luxury Waterproof Mattress is a high-end sleep solution that combines superior comfort, advanced technology, and ultimate protection.",
    "image": "https://cdn.shopify.com/s/files/1/0886/6155/7577/files/luxury-waterproof-mattress-single-3ft-697195.jpg?v=1748473244",
    "variants": [
      {
        "id": 51779286106441,
        "title": "Single (3FT)",
        "price": 23499,
        "compareAtPrice": 58750,
        "available": true
      },
      {
        "id": 51779286139209,
        "title": "Small Double (4FT)",
        "price": 28499,
        "compareAtPrice": 71250,
        "available": true
      },
      {
        "id": 51779286171977,
        "title": "Double (4FT6)",
        "price": 28499,
        "compareAtPrice": 71250,
        "available": true
      },
      {
        "id": 51779286204745,
        "title": "King (5FT)",
        "price": 32499,
        "compareAtPrice": 81250,
        "available": true
      },
      {
        "id": 51779286237513,
        "title": "Super King (6FT)",
        "price": 36499,
        "compareAtPrice": 91250,
        "available": true
      }
    ]
  }
];

    const STEP_PROGRESS = {
      1: 20,
      2: 40,
      3: 60,
      4: 80,
      5: 100,
    };

    const STEP_TITLES = {
      1: { eyebrow: 'Find Your Perfect Mattress', title: 'Step 1 of 4' },
      2: { eyebrow: 'Find Your Perfect Mattress', title: 'Step 2 of 4' },
      3: { eyebrow: 'Find Your Perfect Mattress', title: 'Step 3 of 4' },
      4: { eyebrow: 'Find Your Perfect Mattress', title: 'Step 4 of 4' },
      5: { eyebrow: 'Your Matches', title: 'Best results for your bed size' },
    };

    const tabEyebrow = section.querySelector('#mattressTabEyebrow');
    const tabHeadline = section.querySelector('#mattressTabHeadline');
    const tabSubtitle = section.querySelector('#mattressTabSub');
    const tabChange = section.querySelector('#mattressTabChange');

    const defaultTabState = {
      eyebrow: tabEyebrow ? tabEyebrow.textContent : '',
      headline: tabHeadline ? tabHeadline.textContent : '',
      subtitle: tabSubtitle ? tabSubtitle.textContent : '',
    };

    let answers = {
      sleepers: null,
      position: null,
      feel: null,
      priority: null,
    };

    let currentStep = 1;
    let scoredResults = [];
    let selectedResult = null;

    const sizeOptionIndex = getSizeOptionIndex();

    const mattressVariantInput = ensureMattressPropertyInput('properties[Mattress Add-on Variant ID]');
    const mattressTitleInput = ensureMattressPropertyInput('properties[Mattress Add-on]');

    function ensureMattressPropertyInput(name) {
      if (!productForm) {
        return null;
      }

      let input = productForm.querySelector(`input[name="${name}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = '';
        productForm.appendChild(input);
      }

      return input;
    }

    function updateMattressPropertyInputs() {
      if (!selectedResult) {
        if (mattressVariantInput) {
          mattressVariantInput.value = '';
        }
        if (mattressTitleInput) {
          mattressTitleInput.value = '';
        }
        section.dispatchEvent(
          new CustomEvent('mattress:change', {
            bubbles: true,
          })
        );
        return;
      }

      if (mattressVariantInput) {
        mattressVariantInput.value = String(selectedResult.variant.id || '');
      }

      if (mattressTitleInput) {
        mattressTitleInput.value = `${selectedResult.product.title} - ${selectedResult.variant.title}`;
      }

      section.dispatchEvent(
        new CustomEvent('mattress:change', {
          bubbles: true,
        })
      );
    }

    function getSizeOptionIndex() {
      const tabs = Array.from(section.querySelectorAll('.variant-tab'));

      for (const tab of tabs) {
        const label = (tab.querySelector('.variant-tab-label')?.textContent || '').toLowerCase();
        if (!label.includes('size')) {
          continue;
        }

        const optionIndex = Number(tab.querySelector('[data-tab-current]')?.dataset.optionIndex);
        if (Number.isFinite(optionIndex)) {
          return optionIndex;
        }
      }

      return -1;
    }

    function normalizeSizeKey(rawValue) {
      const value = String(rawValue || '')
        .toLowerCase()
        .replace(/[()]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      if (!value) {
        return '';
      }

      if (/super\s*king|6\s*ft|6ft|180\s*x\s*200|6\s*'\s*0/.test(value)) {
        return 'super-king';
      }

      if (/small\s*single|2\s*ft\s*6|2ft6|75\s*x\s*190|2\s*'\s*6/.test(value)) {
        return 'small-single';
      }

      if (/small\s*double|4\s*ft\b(?!\s*6)|4ft\b(?!6)|120\s*x\s*190|4\s*'\s*0/.test(value)) {
        return 'small-double';
      }

      if (/double|4\s*ft\s*6|4ft6|135\s*x\s*190|4\s*'\s*6/.test(value)) {
        return 'double';
      }

      if (/king|5\s*ft|5ft|150\s*x\s*200|5\s*'\s*0/.test(value)) {
        return 'king';
      }

      if (/single|3\s*ft|3ft|90\s*x\s*190|3\s*'\s*0/.test(value)) {
        return 'single';
      }

      return '';
    }

    function getCurrentSizeLabel() {
      const variant = getCurrentVariant();

      if (!variant || !Array.isArray(variant.options)) {
        return '';
      }

      if (
        Number.isFinite(sizeOptionIndex) &&
        sizeOptionIndex >= 0 &&
        variant.options[sizeOptionIndex]
      ) {
        return variant.options[sizeOptionIndex];
      }

      const firstSizeLike = variant.options.find((optionValue) => normalizeSizeKey(optionValue));
      return firstSizeLike || '';
    }

    function getCurrentSizeKey() {
      return normalizeSizeKey(getCurrentSizeLabel());
    }

    function getVariantForSize(product, currentSizeKey) {
      const variants = Array.isArray(product.variants) ? product.variants : [];
      if (variants.length === 0) {
        return null;
      }

      const lookupOrder = {
        'small-single': ['small-single', 'single'],
        single: ['single'],
        'small-double': ['small-double', 'double'],
        double: ['double', 'small-double'],
        king: ['king'],
        'super-king': ['super-king', 'king'],
      };

      const wantedKeys = lookupOrder[currentSizeKey] || [];

      for (const wantedKey of wantedKeys) {
        const found = variants.find((variant) => {
          if (!variant.available) {
            return false;
          }

          return normalizeSizeKey(variant.title) === wantedKey;
        });

        if (found) {
          return found;
        }
      }

      return variants.find((variant) => variant.available) || variants[0] || null;
    }

    function getFocusableElements() {
      return Array.from(
        mattressDrawer.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.disabled && el.offsetParent !== null);
    }

    function openDrawer() {
      mattressOverlay.classList.add('open');
      mattressDrawer.classList.add('open');
      document.body.style.overflow = 'hidden';

      const quizComplete = answers.sleepers && answers.position && answers.feel && answers.priority;
      goToStep(quizComplete ? 5 : 1);

      const firstCloseBtn = mattressDrawer.querySelector('[data-mattress-close]');
      if (firstCloseBtn) {
        setTimeout(() => firstCloseBtn.focus(), 100);
      }
    }

    function closeDrawer() {
      mattressOverlay.classList.remove('open');
      mattressDrawer.classList.remove('open');
      document.body.style.overflow = '';

      if (mattressTab) {
        mattressTab.focus();
      }
    }

    function formatPrice(cents) {
      return formatMoney(formatter, cents);
    }

    function escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    function shortDescription(text) {
      const cleaned = String(text || '').replace(/\s+/g, ' ').trim();
      if (cleaned.length <= 100) {
        return cleaned;
      }
      return `${cleaned.slice(0, 97)}...`;
    }

    // Curated per-mattress profiles (0-5 scale). Replaces fragile regex matching.
    // Each key is a product handle from MATTRESS_PRODUCTS.
    // feel = how well this mattress satisfies each feel preference.
    // pressure = pressure relief / body conformance (side sleepers, light/sensitive sleepers).
    // back = orthopedic / spinal alignment (back/stomach sleepers, back pain).
    // temp = cooling / breathability (5 = actively cool, 1 = runs warm).
    // motion = motion isolation (couples / restless partners).
    // edge = edge support (couples / sitting on edge).
    // niche = if true, only surfaces when nothing else matches (e.g. waterproof).
    const MATTRESS_PROFILES = {
      'dorchester-orthopedic-mattress': {
        feel: { soft: 1, medium: 3, firm: 5 },
        pressure: 2, back: 5, temp: 2, motion: 2, edge: 4,
      },
      'memory-deluxe-mattress': {
        feel: { soft: 4, medium: 4, firm: 2 },
        pressure: 5, back: 3, temp: 1, motion: 4, edge: 2,
      },
      'oxford-pillowtop-matress': {
        feel: { soft: 4, medium: 4, firm: 2 },
        pressure: 4, back: 3, temp: 3, motion: 2, edge: 3,
      },
      'backcare-1000-mattress': {
        feel: { soft: 1, medium: 2, firm: 5 },
        pressure: 2, back: 5, temp: 3, motion: 3, edge: 4,
      },
      'sapphire-pillowtop-2000-mattress': {
        feel: { soft: 4, medium: 4, firm: 2 },
        pressure: 5, back: 4, temp: 3, motion: 5, edge: 4,
      },
      'super-reflex-foam-mattress': {
        feel: { soft: 3, medium: 4, firm: 3 },
        pressure: 4, back: 4, temp: 1, motion: 4, edge: 3,
      },
      'dorset-pocket-1000-matress': {
        feel: { soft: 2, medium: 4, firm: 4 },
        pressure: 3, back: 4, temp: 3, motion: 3, edge: 3,
      },
      'swansea-2000-pocket-matress': {
        feel: { soft: 4, medium: 4, firm: 2 },
        pressure: 4, back: 3, temp: 3, motion: 5, edge: 4,
      },
      'tencel-1500-pocket-mattress': {
        feel: { soft: 3, medium: 4, firm: 3 },
        pressure: 3, back: 4, temp: 5, motion: 4, edge: 4,
      },
      'chester-wool-2000-pocket-mattress': {
        feel: { soft: 5, medium: 3, firm: 1 },
        pressure: 4, back: 3, temp: 5, motion: 5, edge: 4,
      },
      'mroydon-silk-1500-pocket-mattress': {
        feel: { soft: 3, medium: 4, firm: 3 },
        pressure: 3, back: 4, temp: 3, motion: 4, edge: 4,
      },
      'silk-memory-1000-mattress': {
        feel: { soft: 4, medium: 4, firm: 2 },
        pressure: 4, back: 3, temp: 2, motion: 3, edge: 3,
      },
      'backcare-2000-mattress': {
        feel: { soft: 1, medium: 2, firm: 5 },
        pressure: 3, back: 5, temp: 3, motion: 4, edge: 4,
      },
      'richmond-pillowtop-3000-pocket': {
        feel: { soft: 4, medium: 4, firm: 2 },
        pressure: 5, back: 4, temp: 3, motion: 5, edge: 5,
      },
      'oxford-mattress': {
        feel: { soft: 2, medium: 4, firm: 3 },
        pressure: 2, back: 3, temp: 3, motion: 2, edge: 3,
      },
      'supreme-2500-pocket-matress': {
        feel: { soft: 4, medium: 4, firm: 2 },
        pressure: 4, back: 4, temp: 3, motion: 5, edge: 4,
      },
      'luxury-waterproof-mattress': {
        feel: { soft: 2, medium: 3, firm: 3 },
        pressure: 3, back: 3, temp: 3, motion: 2, edge: 2,
        niche: true,
      },
    };

    const DEFAULT_PROFILE = {
      feel: { soft: 2, medium: 3, firm: 2 },
      pressure: 2, back: 2, temp: 3, motion: 2, edge: 2,
    };

    function getProfile(product) {
      return MATTRESS_PROFILES[product.handle] || DEFAULT_PROFILE;
    }

    function scoreResult(result) {
      const profile = result.profile;
      let score = 0;
      const reasons = [];

      // Q1: Sleepers
      if (answers.sleepers === 'couple') {
        score += profile.motion * 3;
        score += profile.edge * 2;
        if (profile.motion >= 4) reasons.push('Great motion isolation');
        else if (profile.edge >= 4) reasons.push('Strong edge support');
      } else if (answers.sleepers === 'solo') {
        score += profile.pressure * 2;
      }

      // Q2: Sleep position (biggest predictor of firmness fit)
      if (answers.position === 'side') {
        score += profile.feel.soft * 4;
        score += profile.pressure * 3;
        if (profile.feel.soft >= 4 && profile.pressure >= 4) {
          reasons.push('Soft & cushioning for side sleepers');
        }
      } else if (answers.position === 'back') {
        score += profile.feel.medium * 4;
        score += profile.back * 2;
        if (profile.feel.medium >= 4 && profile.back >= 4) {
          reasons.push('Balanced support for back sleepers');
        }
      } else if (answers.position === 'stomach') {
        score += profile.feel.firm * 4;
        score += profile.back * 2;
        if (profile.feel.firm >= 4) reasons.push('Firm support for stomach sleepers');
      } else if (answers.position === 'combo') {
        score += profile.feel.medium * 4;
        score += profile.motion * 2;
      }

      // Q3: Feel preference (direct match)
      if (answers.feel && profile.feel[answers.feel] != null) {
        score += profile.feel[answers.feel] * 5;
      }

      // Q4: Priority (strongest dedicated weight)
      if (answers.priority === 'pressure') {
        score += profile.pressure * 6;
        if (profile.pressure >= 4) reasons.push('Excellent pressure relief');
      } else if (answers.priority === 'back') {
        score += profile.back * 6;
        if (profile.back >= 4) reasons.push('Excellent back support');
      } else if (answers.priority === 'cool') {
        score += profile.temp * 8;
        if (profile.temp >= 4) reasons.push('Cool & breathable');
      }

      // Niche products (e.g. waterproof) get a fixed penalty so they
      // never surface as a top match unless the curated catalog is sparse.
      if (profile.niche) score -= 30;

      // Light price tie-break (cheaper wins when score is equal)
      const priceTieBreak = Math.max(0, 3 - Math.floor(Number(result.variant.price || 0) / 20000));

      result.reasons = reasons;
      return score * 100 + priceTieBreak;
    }

    function updateStepUi(stepNumber) {
      currentStep = stepNumber;

      steps.forEach((step) => {
        const isActive = Number(step.dataset.quizStep) === stepNumber;
        step.classList.toggle('is-active', isActive);
      });

      if (progressFill) {
        progressFill.style.width = `${STEP_PROGRESS[stepNumber] || 0}%`;
      }

      dotNodes.forEach((dot) => {
        const dotStep = Number(dot.dataset.mattressDot);
        dot.classList.remove('is-active', 'is-done');

        if (dotStep === stepNumber) {
          dot.classList.add('is-active');
        } else if (dotStep < stepNumber) {
          dot.classList.add('is-done');
        }
      });

      const stepMeta = STEP_TITLES[stepNumber] || STEP_TITLES[1];
      if (drawerEyebrow) {
        drawerEyebrow.textContent = stepMeta.eyebrow;
      }
      if (drawerTitle) {
        drawerTitle.textContent = stepMeta.title;
      }

      const stepElement = steps.find((step) => Number(step.dataset.quizStep) === stepNumber);
      if (stepElement && stepNumber < 5) {
        const answerKey = stepElement.querySelector('[data-quiz-answer]')?.dataset.quizAnswer;
        const answerValue = answerKey ? answers[answerKey] : null;

        stepElement.querySelectorAll('[data-quiz-answer]').forEach((option) => {
          option.classList.toggle('is-selected', option.dataset.quizValue === answerValue);
        });

        const nextBtn = stepElement.querySelector('.quiz-next');
        if (nextBtn) {
          nextBtn.disabled = !answerValue;
        }
      }

      if (stepNumber === 5) {
        renderResults();
      }
    }

    function goToStep(stepNumber) {
      const targetStep = Math.max(1, Math.min(5, Number(stepNumber) || 1));
      updateStepUi(targetStep);
    }

    function buildCardMarkup(result, isSelected, isBest) {
      const { product, variant } = result;
      const currentPrice = Number(variant.price || 0);
      const comparePrice = Number(variant.compareAtPrice || 0);
      const hasDiscount = comparePrice > currentPrice;
      const saveCents = hasDiscount ? comparePrice - currentPrice : 0;

      const saveLabel = hasDiscount
        ? `<div class="pdp-save-price">Save ${formatPrice(saveCents)}</div>`
        : '';

      const wasLabel = hasDiscount
        ? `<span class="mattress-price-was">Was ${formatPrice(comparePrice)}</span>`
        : '';

      const bestLabel = isBest
        ? '<div class="mattress-match-badge">Top Match</div>'
        : `<div class="mattress-match-badge">Size: ${escapeHtml(variant.title)}</div>`;

      const topReason = (result.reasons && result.reasons.length > 0) ? result.reasons[0] : '';
      const reasonLabel = topReason
        ? `<div class="mattress-reason"><span class="mi" aria-hidden="true">check_circle</span><span>${escapeHtml(topReason)}</span></div>`
        : '';

      return `
        <div class="card-addons${isSelected ? ' is-selected' : ''}" data-product-id="${product.productId}" data-variant-id="${variant.id}" data-mattress-handle="${escapeHtml(product.handle)}">
          <div class="card-body-mattress-drawer" data-mattress-option-card data-variant-id="${variant.id}" data-mattress-handle="${escapeHtml(product.handle)}">
            <span class="add-plus-btn" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </span>
            <div class="image-card">
              <img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" height="80" width="86" loading="lazy">
            </div>
            <div class="card-details add-on">
              <div class="mattress-card-title">${escapeHtml(product.title)}</div>
              ${reasonLabel}
              <div class="mattress-card-description">${escapeHtml(shortDescription(product.description))}</div>
              <div class="card-pricing-details">
                <div class="mattress-price-row">
                  <span class="mattress-price-current">${formatPrice(currentPrice)}</span>
                  ${wasLabel}
                </div>
                <div class="mattress-meta-row">
                  ${bestLabel}
                  ${saveLabel}
                  <button
                    type="button"
                    class="mattress-see-details"
                    data-mattress-see-details
                    data-mattress-handle="${escapeHtml(product.handle)}"
                    data-mattress-url="/products/${escapeHtml(product.handle)}"
                  >See details</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    function updateCardSelection() {
      const selectedVariantId = selectedResult ? String(selectedResult.variant.id) : '';

      mattressList.querySelectorAll('.card-addons').forEach((card) => {
        const isSelected = card.dataset.variantId === selectedVariantId;
        card.classList.toggle('is-selected', isSelected);
      });
    }

    function updateMattressTabState() {
      if (!selectedResult) {
        mattressTab.classList.remove('is-added');
        mattressTab.setAttribute('aria-label', 'Open mattress finder');

        if (tabEyebrow) {
          tabEyebrow.textContent = defaultTabState.eyebrow;
        }
        if (tabHeadline) {
          tabHeadline.textContent = defaultTabState.headline;
        }
        if (tabSubtitle) {
          tabSubtitle.textContent = defaultTabState.subtitle;
        }
        if (tabChange) {
          tabChange.classList.remove('is-visible');
        }

        return;
      }

      const { product, variant } = selectedResult;
      const comparePrice = Number(variant.compareAtPrice || 0);
      const price = Number(variant.price || 0);
      const hasDiscount = comparePrice > price;

      mattressTab.classList.add('is-added');
      mattressTab.setAttribute('aria-label', 'Edit selected mattress');

      if (tabEyebrow) {
        tabEyebrow.textContent = 'Mattress selected';
      }

      if (tabHeadline) {
        tabHeadline.innerHTML = `<strong>${escapeHtml(product.title)}</strong>`;
      }

      if (tabSubtitle) {
        if (hasDiscount) {
          tabSubtitle.textContent = `${variant.title} · ${formatPrice(price)} · Save ${formatPrice(comparePrice - price)}`;
        } else {
          tabSubtitle.textContent = `${variant.title} · ${formatPrice(price)}`;
        }
      }

      if (tabChange) {
        tabChange.classList.add('is-visible');
      }
    }

    function selectResult(result) {
      selectedResult = {
        product: result.product,
        variant: result.variant,
      };

      updateCardSelection();
      updateMattressPropertyInputs();
      updateMattressTabState();
    }

    function bindResultEvents() {
      mattressList.querySelectorAll('[data-mattress-option-card]').forEach((card) => {
        card.addEventListener('click', () => {
          const variantId = Number(card.dataset.variantId);
          const handle = card.dataset.mattressHandle || '';

          const match = scoredResults.find(
            (result) =>
              String(result.product.handle) === String(handle) && Number(result.variant.id) === variantId
          );

          if (match) {
            selectResult(match);
          }
        });
      });

      mattressList.querySelectorAll('[data-mattress-see-details]').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          const url = button.dataset.mattressUrl;
          if (url) {
            window.open(url, '_blank', 'noopener');
          }
        });
      });
    }

    function renderResults() {
      const sizeLabel = getCurrentSizeLabel() || 'your selected size';
      const sizeKey = getCurrentSizeKey();

      if (sizeLabelNode) {
        sizeLabelNode.textContent = sizeLabel;
      }

      const results = MATTRESS_PRODUCTS.map((product) => {
        const variant = getVariantForSize(product, sizeKey);
        if (!variant) {
          return null;
        }

        return {
          product,
          variant,
          profile: getProfile(product),
          reasons: [],
          score: 0,
        };
      }).filter(Boolean);

      results.forEach((result) => {
        result.score = scoreResult(result);
      });

      results.sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }

        const priceDiff = Number(a.variant.price || 0) - Number(b.variant.price || 0);
        if (priceDiff !== 0) {
          return priceDiff;
        }

        return String(a.product.title).localeCompare(String(b.product.title));
      });

      scoredResults = results;

      if (scoredResults.length === 0) {
        if (recoTitleNode) {
          recoTitleNode.textContent = 'No mattresses found for this size yet.';
        }

        mattressList.innerHTML = '<p class="text-gray fs-sm">No mattress matches are currently available for this size.</p>';
        return;
      }

      const topMatch = scoredResults[0];
      if (recoTitleNode) {
        recoTitleNode.textContent = `${topMatch.product.title} — ${topMatch.variant.title}`;
      }

      // Always auto-select the current quiz top match.
      selectedResult = {
        product: topMatch.product,
        variant: topMatch.variant,
      };

      const isSelectedFor = (result) =>
        selectedResult &&
        selectedResult.product.handle === result.product.handle &&
        Number(selectedResult.variant.id) === Number(result.variant.id);

      const topCardHtml = `
        <div class="mattress-result-group mattress-result-group--top">
          <div class="mattress-result-group-label">
            <span class="mi" aria-hidden="true">stars</span>
            <span>Recommended for you</span>
          </div>
          ${buildCardMarkup(topMatch, isSelectedFor(topMatch), true)}
        </div>
      `;

      const restResults = scoredResults.slice(1);
      const restCardsHtml = restResults.length === 0
        ? ''
        : `
          <div class="mattress-result-group mattress-result-group--others">
            <div class="mattress-result-group-label mattress-result-group-label--others">Other options</div>
            ${restResults.map((result) => buildCardMarkup(result, isSelectedFor(result), false)).join('')}
          </div>
        `;

      mattressList.innerHTML = topCardHtml + restCardsHtml;

      bindResultEvents();
      updateMattressPropertyInputs();
      updateMattressTabState();
      updateCardSelection();
    }

    function updateSelectedVariantForSize() {
      if (!selectedResult) {
        return;
      }

      const matchedVariant = getVariantForSize(selectedResult.product, getCurrentSizeKey());
      if (!matchedVariant) {
        return;
      }

      selectedResult = {
        product: selectedResult.product,
        variant: matchedVariant,
      };

      updateMattressPropertyInputs();
      updateMattressTabState();
      updateCardSelection();
    }

    mattressTab.addEventListener('click', (event) => {
      const arrowContainer = mattressTab.querySelector('.mattress-tab-arrow');
      const isCloseClick = arrowContainer && (event.target === arrowContainer || arrowContainer.contains(event.target));
      if (isCloseClick && mattressTab.classList.contains('is-added')) {
        event.stopPropagation();
        selectedResult = null;
        updateMattressPropertyInputs();
        updateMattressTabState();
        updateCardSelection();
        return;
      }
      openDrawer();
    });

    if (typeof window !== 'undefined' && window.location && window.location.hash === '#mattressTab') {
      requestAnimationFrame(() => {
        try {
          mattressTab.scrollIntoView({ behavior: 'smooth', block: 'center' });
          openDrawer();
        } catch (e) {
          openDrawer();
        }
      });
    }

    closeButtons.forEach((button) => {
      button.addEventListener('click', closeDrawer);
    });

    mattressOverlay.addEventListener('click', closeDrawer);

    mattressDrawer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeDrawer();
        return;
      }

      if (event.key === 'Tab') {
        const focusableElements = getFocusableElements();
        if (focusableElements.length === 0) {
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    });

    mattressDrawer.querySelectorAll('[data-quiz-answer]').forEach((option) => {
      option.addEventListener('click', () => {
        const step = option.closest('[data-quiz-step]');
        if (!step) {
          return;
        }

        const answerKey = option.dataset.quizAnswer;
        const answerValue = option.dataset.quizValue;

        if (!answerKey) {
          return;
        }

        selectedResult = null;
        updateMattressPropertyInputs();
        updateMattressTabState();

        answers[answerKey] = answerValue;

        step.querySelectorAll(`[data-quiz-answer="${answerKey}"]`).forEach((stepOption) => {
          stepOption.classList.remove('is-selected');
        });

        option.classList.add('is-selected');

        const nextBtn = step.querySelector('.quiz-next');
        if (nextBtn) {
          nextBtn.disabled = false;
        }
      });
    });

    mattressDrawer.querySelectorAll('[data-quiz-next]').forEach((button) => {
      button.addEventListener('click', () => {
        const nextStep = Number(button.dataset.quizNext || 0);
        if (nextStep) {
          goToStep(nextStep);
        }
      });
    });

    mattressDrawer.querySelectorAll('[data-quiz-back]').forEach((button) => {
      button.addEventListener('click', () => {
        const previousStep = Number(button.dataset.quizBack || 0);
        if (previousStep) {
          goToStep(previousStep);
        }
      });
    });

    section.addEventListener('variant:change', () => {
      updateSelectedVariantForSize();

      if (currentStep === 4 && mattressDrawer.classList.contains('open')) {
        renderResults();
      } else if (sizeLabelNode) {
        sizeLabelNode.textContent = getCurrentSizeLabel() || 'your selected size';
      }
    });

    updateMattressPropertyInputs();
    updateMattressTabState();
    updateStepUi(1);

    return {
      hasSelection() {
        return Boolean(selectedResult && selectedResult.variant && selectedResult.variant.id);
      },
      getSelectedVariantId() {
        return selectedResult && selectedResult.variant ? String(selectedResult.variant.id || '') : '';
      },
      open: openDrawer,
      close: closeDrawer,
    };
  }


  function initProductRecommendations() {
    const placeholders = document.querySelectorAll('.product-recommendations-placeholder');

    placeholders.forEach((placeholder) => {
      const baseUrl = placeholder.dataset.baseUrl;
      const productId = placeholder.dataset.productId;
      const limit = placeholder.dataset.limit;

      if (!baseUrl || !productId) {
        return;
      }

      const sectionId = placeholder.closest('[data-section-id]')?.dataset.sectionId;

      fetch(`${baseUrl}?section_id=${sectionId}&product_id=${productId}&limit=${limit}`)
        .then((response) => response.text())
        .then((text) => {
          const template = document.createElement('template');
          template.innerHTML = text;
          const content = template.content.querySelector('.related-grid');

          if (content) {
            placeholder.replaceWith(content);
            // Initialize animations for new content
            const newElements = content.querySelectorAll('.afu');
            newElements.forEach((el) => el.classList.add('is-visible'));
          }
        })
        .catch((error) => {
          console.error('Failed to load product recommendations:', error);
        });
    });
  }

  function initFabricTabs(scope) {
    scope.querySelectorAll('[data-fabric-tabs]').forEach((tabsContainer) => {
      const tabs = tabsContainer.querySelectorAll('[data-fabric-tab]');
      const panel = tabsContainer.closest('.variant-panel, [data-product-color-options]');
      if (!panel) return;

      const grids = panel.querySelectorAll('[data-fabric-panel]');

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const fabricHandle = tab.dataset.fabricTab;

          // Update active tab
          tabs.forEach((t) => {
            const isActive = t === tab;
            t.classList.toggle('is-active', isActive);
            t.setAttribute('aria-pressed', String(isActive));
          });

          // Show corresponding grid
          grids.forEach((grid) => {
            if (grid.dataset.fabricPanel === fabricHandle) {
              grid.classList.add('is-active');
            } else {
              grid.classList.remove('is-active');
            }
          });
        });
      });
    });
  }

  onReady(() => {
    initProductRecommendations();
    initInfoTabs(document);
    initDimensionsTabs(document);
    initFaq(document);
    initChat(document);
    initPageAnchors(document);
    initRevealAnimation(document);
    initFabricTabs(document);

    document.querySelectorAll('[data-product-section]').forEach((section) => {
      initProductSection(section);
    });
  });
})();
